/**
 * Trovva — Deal Detail Drawer
 *
 * Right-side drawer for a single transaction. Pinned header +
 * 7 tabs:
 *   Overview   key financial + status at-a-glance, editable
 *   Milestones NJ-specific checklist — attorney review → inspection →
 *              appraisal → mortgage commitment → clear to close → closed
 *   Parties    co-op agent, attorney, lender, title, inspector contacts
 *   Commission split calc, agent/brokerage payouts
 *   Tasks      tasks for this deal's linked contact
 *   Notes      timestamped team notes + @mentions + milestone markers
 *   Docs       docs JSONB (local) + Dotloop loop link (when integrated)
 */
import { useState, useEffect, useCallback } from 'react';
import {
  X, Info, ListChecks, Users, DollarSign, CheckCircle2, FileText,
  FolderOpen, Phone, Mail, Loader2, AlertCircle, Calendar, Briefcase,
  ArrowRight, Check, Plus, Send, Trash2, ExternalLink, Home,
  ClipboardList, Upload, Shield, RotateCcw, Eye, Flag,
  MapPin, User, Clock, Building2, Landmark, Scale, History,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';
import { formatDate, formatDateShort } from '../../lib/dateFormat';
import DealRoomPanel from './DealRoomPanel';
import ClientInvitePanel from './ClientInvitePanel';
import PdfFormFiller from './PdfFormFiller';

const TABS = [
  { key: 'overview',    label: 'Overview',    icon: Info },
  { key: 'dealroom',    label: 'Deal Room',   icon: Home },
  { key: 'milestones',  label: 'Milestones',  icon: ListChecks },
  { key: 'compliance',  label: 'Compliance',  icon: Shield },
  { key: 'parties',     label: 'Parties',     icon: Users },
  { key: 'commission',  label: 'Commission',  icon: DollarSign },
  { key: 'tasks',       label: 'Tasks',       icon: CheckCircle2 },
  { key: 'notes',       label: 'Notes',       icon: FileText },
  { key: 'traceability', label: 'Traceability', icon: History },
];

const STATUS_META = {
  // ── Sales (pre-contract) ──
  new_lead:             { label: 'New lead',             color: '#94A3B8' },
  contacted:            { label: 'Contacted',            color: '#64748B' },
  qualified:            { label: 'Qualified',            color: theme.colors.info },
  active_search:        { label: 'Active search',        color: '#0EA5E9' },
  offer_submitted:      { label: 'Offer submitted',      color: '#3B82F6' },
  offer_accepted:       { label: 'Offer accepted',       color: '#7C3AED' },
  // ── Transaction (post-contract, NJ) ──
  attorney_review:      { label: 'Attorney review',      color: theme.colors.warning },
  under_contract:       { label: 'Under contract',       color: theme.colors.accent },
  inspection:           { label: 'Inspection',           color: '#F59E0B' },
  appraisal:            { label: 'Appraisal',            color: '#F97316' },
  mortgage_commitment:  { label: 'Mortgage commitment',  color: '#6366F1' },
  clear_to_close:       { label: 'Clear to close',       color: theme.colors.success },
  closed:               { label: 'Closed',               color: theme.colors.primary },
  // ── Terminal ──
  fell_through:         { label: 'Fell through',         color: theme.colors.danger },
  cancelled:            { label: 'Cancelled',            color: theme.colors.textLight },
};

// Chronological milestone timeline. Each entry has a date column it
// maps to on the deal and a status the deal advances to on completion.
const RESIDENTIAL_MILESTONES = [
  { key: 'attorney_review',     label: 'Attorney review',     dateCol: 'attorney_review_end' },
  { key: 'inspection',          label: 'Inspection',          dateCol: 'inspection_date' },
  { key: 'appraisal',           label: 'Appraisal',           dateCol: 'appraisal_date' },
  { key: 'mortgage_commitment', label: 'Mortgage commitment', dateCol: 'mortgage_commitment_date' },
  { key: 'clear_to_close',      label: 'Clear to close',      dateCol: 'clear_to_close_date' },
  { key: 'closed',              label: 'Closed',              dateCol: 'actual_closing_date' },
];

// Commercial SALE milestones — matches COMMERCIAL_SALE_STAGES Kanban
const COMMERCIAL_SALE_MILESTONES = [
  { key: 'loi_accepted',        label: 'LOI Accepted',               dateCol: 'loi_accepted_date' },
  { key: 'due_diligence',       label: 'Due Diligence',              dateCol: 'due_diligence_end' },
  { key: 'purchase_agreement',  label: 'Purchase Agreement',         dateCol: 'attorney_review_end' },
  { key: 'estoppels_snda',      label: 'Estoppels / SNDA',           dateCol: 'mortgage_commitment_date' },
  { key: 'financing',           label: 'Financing / UW',             dateCol: 'clear_to_close_date' },
  { key: 'clear_to_close',      label: 'Clear to Close',             dateCol: 'clear_to_close_date' },
  { key: 'closed',              label: 'Closed',                     dateCol: 'actual_closing_date' },
];

// Commercial LEASE milestones — matches COMMERCIAL_LEASE_STAGES Kanban
const COMMERCIAL_LEASE_MILESTONES = [
  { key: 'loi_accepted',        label: 'LOI Accepted',               dateCol: 'loi_accepted_date' },
  { key: 'due_diligence',       label: 'Due Diligence',              dateCol: 'due_diligence_end' },
  { key: 'lease_draft',         label: 'Lease Draft',                dateCol: 'lease_execution_date' },
  { key: 'lease_executed',      label: 'Lease Executed',             dateCol: 'lease_execution_date' },
  { key: 'build_out',           label: 'Build-Out / TI',             dateCol: 'build_out_end' },
  { key: 'occupancy',           label: 'Occupancy',                  dateCol: 'occupancy_date' },
];

// Legacy alias
const COMMERCIAL_MILESTONES = COMMERCIAL_SALE_MILESTONES;

const isCommercialDeal = (deal) => ['commercial_sale', 'commercial_lease'].includes(deal?.deal_type);
const getMilestoneFlow = (deal) => {
  if (deal?.deal_type === 'commercial_lease') return COMMERCIAL_LEASE_MILESTONES;
  if (deal?.deal_type === 'commercial_sale') return COMMERCIAL_SALE_MILESTONES;
  return RESIDENTIAL_MILESTONES;
};

// Keep backward-compatible alias
const MILESTONE_FLOW = RESIDENTIAL_MILESTONES;

export default function DealDetailDrawer({ dealId, onClose, onChange, showToast }) {
  const [tab, setTab] = useState('overview');
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lostDealModal, setLostDealModal] = useState(false);

  const load = useCallback(async () => {
    if (!dealId) return;
    setLoading(true); setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const r = await fetch(`/api/deals?id=${dealId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed');
      const d = data.deal;
      // Auto-fill agent_split_pct from agent's profile if not set on this deal
      if (d && d.agent_id && d.agent_split_pct == null) {
        try {
          const agentRes = await fetch(`/api/team`, { headers: { Authorization: `Bearer ${session.access_token}` } });
          if (agentRes.ok) {
            const agentData = await agentRes.json();
            const agent = (agentData.team || agentData.members || []).find(u => u.id === d.agent_id);
            if (agent?.commission_split) {
              d.agent_split_pct = Number(agent.commission_split);
              // Save it to the deal so it persists
              fetch(`/api/deals?id=${dealId}`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ agent_split_pct: d.agent_split_pct }),
              }).catch(() => {}); // fire-and-forget
            }
          }
        } catch {} // non-fatal
      }
      setDeal(d);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  const update = async (patch) => {
    setSaving(true); setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/deals?id=${dealId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed');
      setDeal(data.deal);
      onChange?.(data.deal);
    } catch (e) { setError(e.message); showToast?.(e.message); }
    finally { setSaving(false); }
  };

  const advanceMilestone = async (milestoneKey, note) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/deals?action=milestone_done', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ deal_id: dealId, milestone: milestoneKey, note: note || null }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed');
      setDeal(data.deal);
      onChange?.(data.deal);
      showToast?.(`${milestoneKey.replace(/_/g, ' ')} marked done`);
    } catch (e) { showToast?.(e.message); }
  };

  if (!dealId) return null;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 400,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: 1100, background: theme.colors.card,
        height: 'calc(100vh - 80px)', maxHeight: 800,
        borderRadius: 16, overflow: 'hidden',
        boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
        display: 'flex', flexDirection: 'column',
      }}>
        {loading && <Center><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</Center>}
        {error && !loading && <Center><AlertCircle size={16} color={theme.colors.danger} /> {error}</Center>}

        {deal && (
          <>
            <DrawerHeader deal={deal} saving={saving} onClose={onClose} />

            {/* Tabs — full width, no scrolling */}
            <div style={{
              display: 'flex', gap: 0, padding: '0 18px',
              borderBottom: `1px solid ${theme.colors.border}`,
              flexShrink: 0, background: theme.colors.card,
            }}>
              {TABS.map(t => {
                const Icon = t.icon;
                const active = tab === t.key;
                return (
                  <button key={t.key} onClick={() => setTab(t.key)} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '10px 14px', border: 'none', background: 'transparent', cursor: 'pointer',
                    borderBottom: active ? `2px solid ${theme.colors.primary}` : '2px solid transparent',
                    color: active ? theme.colors.primary : theme.colors.textLight,
                    fontWeight: active ? 700 : 500, fontSize: 12, whiteSpace: 'nowrap',
                    marginBottom: -1, transition: 'color 0.15s',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.color = theme.colors.text; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.color = theme.colors.textLight; }}
                  >
                    <Icon size={13} /> {t.label}
                  </button>
                );
              })}
            </div>

            {/* Tab content — scrollable, wider for deal room tab */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              <div style={{ maxWidth: tab === 'dealroom' ? 960 : 720, margin: '0 auto', transition: 'max-width 0.2s' }}>
              {tab === 'overview'   && <OverviewTab deal={deal} onUpdate={update} onTabSwitch={setTab} onStatusChange={(newStatus) => { if (['fell_through', 'cancelled'].includes(newStatus)) setLostDealModal(true); }} />}
              {tab === 'milestones' && <MilestonesTab deal={deal} onAdvance={advanceMilestone} />}
              {tab === 'compliance' && <ComplianceTab deal={deal} showToast={showToast} />}
              {tab === 'parties'    && <PartiesTab deal={deal} onUpdate={update} />}
              {tab === 'commission' && <CommissionTab deal={deal} onUpdate={update} />}
              {tab === 'tasks'      && <DealTasksTab deal={deal} showToast={showToast} />}
              {tab === 'notes'      && <NotesTab dealId={dealId} showToast={showToast} />}
              {tab === 'traceability' && <TraceabilityTab dealId={dealId} />}
              {tab === 'dealroom'   && <DocsTab deal={deal} onUpdate={update} showToast={showToast} />}
              </div>
            </div>
          </>
        )}
        <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
      </div>
      {lostDealModal && deal && (
        <LostDealModal deal={deal} onClose={() => setLostDealModal(false)} showToast={showToast} />
      )}
    </div>
  );
}

// ── Header ──────────────────────────────────────────────────────
function DrawerHeader({ deal, saving, onClose }) {
  let address = deal.property?.address
    ? `${deal.property.address}${deal.property.city ? ', ' + deal.property.city : ''}${deal.property.state ? ' ' + deal.property.state : ''}`
    : deal.property_address || null;
  const clientName = deal.contact ? `${deal.contact.first_name} ${deal.contact.last_name}`.trim() : null;
  if (!address) address = clientName ? `${clientName} — Transaction` : 'Transaction';
  const stat = STATUS_META[deal.status] || { label: deal.status, color: theme.colors.textLight };
  const price = deal.sale_price || deal.offer_price;

  return (
    <div style={{
      padding: '18px 24px 14px', borderBottom: `1px solid ${theme.colors.border}`, flexShrink: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 14,
          background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryDark})`,
          color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}><Briefcase size={22} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 20, color: theme.colors.text, fontWeight: 700 }}>{address}</h2>
            {saving && <Loader2 size={12} style={{ animation: 'spin 1s linear infinite', color: theme.colors.textLight }} />}
          </div>
          <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 3, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <StatusBadge stat={stat} />
            {clientName && <span>· {clientName}</span>}
            {price && <span>· {fmtMoney(price)}</span>}
            {deal.deal_type && <span>· {({ buyer: 'Purchase', seller: 'Sale', lease: 'Lease', commercial_sale: 'Commercial Sale', commercial_lease: 'Commercial Lease' })[deal.deal_type] || deal.deal_type}</span>}
          </div>
          {deal.closing_date && (
            <div style={{ fontSize: 11, color: theme.colors.primary, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Calendar size={11} /> Closing: {formatDate(deal.closing_date)}
            </div>
          )}
        </div>
        <button onClick={onClose} style={iconBtn()}><X size={16} /></button>
      </div>
    </div>
  );
}

// ── Tabs ────────────────────────────────────────────────────────
function OverviewTab({ deal, onUpdate, onTabSwitch, onStatusChange }) {
  const next = nextMilestone(deal);
  const milestones = getMilestoneFlow(deal);
  const currentIdx = milestones.findIndex(m => m.key === deal.status);
  const stat = STATUS_META[deal.status] || { label: deal.status, color: theme.colors.textLight };

  // Progress calculation
  const completedCount = milestones.filter((m, i) => !!deal[m.dateCol] || (currentIdx >= 0 && i < currentIdx)).length;
  const progressPct = milestones.length > 0 ? Math.round((completedCount / milestones.length) * 100) : 0;

  // Financials
  const salePrice = Number(deal.sale_price || 0);
  const gross = Number(deal.commission_amount || 0);
  const agentPayout = Number(deal.agent_payout || 0);
  const brokeragePayout = Number(deal.brokerage_payout || 0);

  const clientName = deal.contact ? `${deal.contact.first_name || ''} ${deal.contact.last_name || ''}`.trim() : null;
  const address = deal.property_address || deal.property?.address || null;
  const dealTypeLabel = ({ buyer: 'Purchase', seller: 'Sale', lease: 'Lease', commercial_sale: 'Commercial Sale', commercial_lease: 'Commercial Lease' })[deal.deal_type] || deal.deal_type;

  // Days to closing
  const daysToClose = deal.closing_date ? Math.ceil((new Date(deal.closing_date + 'T00:00:00') - new Date()) / 86400000) : null;

  // Contextual action guidance based on current status
  const ACTION_GUIDANCE = {
    attorney_review: {
      icon: '📝',
      title: 'Send contract for e-signature',
      description: 'The offer document is in your Deal Room. Send it to both attorneys and parties for signature via BoldSign.',
      action: 'Go to Deal Room',
      tab: 'dealroom',
      color: '#8B5CF6',
    },
    inspection: {
      icon: '🔍',
      title: 'Schedule home inspection',
      description: 'Attorney review is complete. Schedule the inspection and upload the report when done.',
      action: 'Add Parties',
      tab: 'parties',
      color: '#06B6D4',
    },
    appraisal: {
      icon: '🏠',
      title: 'Order appraisal',
      description: 'Inspection is done. The lender will order the appraisal — track it in Milestones.',
      action: 'View Milestones',
      tab: 'milestones',
      color: '#F59E0B',
    },
    mortgage_commitment: {
      icon: '🏦',
      title: 'Obtain mortgage commitment',
      description: 'Appraisal is complete. Follow up with the lender for the commitment letter.',
      action: 'View Milestones',
      tab: 'milestones',
      color: '#2563EB',
    },
    clear_to_close: {
      icon: '✅',
      title: 'Final walkthrough & closing prep',
      description: 'All conditions met. Schedule the final walkthrough and confirm closing date with the title company.',
      action: 'Check Paperwork',
      tab: 'paperwork',
      color: '#16A34A',
    },
  };

  const guidance = ACTION_GUIDANCE[deal.status];

  return (
    <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Contextual action card — what should the broker do NOW */}
      {guidance && (
        <div style={{
          padding: 14, borderRadius: 12,
          background: `${guidance.color}08`, border: `1px solid ${guidance.color}25`,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: `${guidance.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18,
            }}>{guidance.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>{guidance.title}</div>
              <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 3, lineHeight: 1.5 }}>
                {guidance.description}
              </div>
              <button onClick={() => onTabSwitch?.(guidance.tab)} style={{
                marginTop: 8, padding: '6px 12px', borderRadius: 8, border: 'none',
                background: guidance.color, color: 'white',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
                {guidance.action} <ArrowRight size={12} />
              </button>
            </div>
          </div>
        </div>
      )}

      {deal.last_checkpoint_note && (
        <div style={{
          padding: 12, background: '#EEF7F5', border: `1px solid ${theme.colors.primary}30`,
          borderRadius: 12, display: 'flex', gap: 10, alignItems: 'flex-start',
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
            background: `${theme.colors.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><Check size={12} color={theme.colors.primary} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: theme.colors.primary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Last checkpoint
            </div>
            <div style={{ fontSize: 13, color: theme.colors.text, marginTop: 3 }}>{deal.last_checkpoint_note}</div>
            {deal.last_checkpoint_at && (
              <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 2 }}>
                {new Date(deal.last_checkpoint_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </div>
            )}
          </div>
        </div>
      )}

      {next && !guidance && (
        <div style={{
          padding: 12, background: '#FFF7ED', border: `1px solid ${theme.colors.accent}30`,
          borderRadius: 12, display: 'flex', gap: 10, alignItems: 'flex-start',
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
            background: `${theme.colors.accent}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><ArrowRight size={12} color={theme.colors.accent} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: theme.colors.accent, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Next up
            </div>
            <div style={{ fontSize: 13, color: theme.colors.text, marginTop: 2 }}>{next.label}</div>
          </div>
        </div>
      )}

      {/* ── Deal Summary Card ─────────────────────────────────── */}
      <div style={{
        padding: 16, borderRadius: 12,
        background: `linear-gradient(135deg, ${theme.colors.primary}05, ${theme.colors.primary}02)`,
        border: `1px solid ${theme.colors.primary}18`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
          <Home size={14} color={theme.colors.primary} />
          <span style={{ fontSize: 11, fontWeight: 700, color: theme.colors.primary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Deal Summary</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <SummaryItem icon={<MapPin size={13} color="#8B5CF6" />} iconBg="#8B5CF608" label="Property" value={address || '—'} />
          <SummaryItem icon={<User size={13} color={theme.colors.primary} />} iconBg={`${theme.colors.primary}08`} label="Client" value={clientName || '—'} />
          <SummaryItem icon={<DollarSign size={13} color={theme.colors.success} />} iconBg={`${theme.colors.success}08`} label="Sale Price" value={salePrice > 0 ? fmtMoney(salePrice) : '—'} bold={salePrice > 0} />
          <SummaryItem icon={<Calendar size={13} color={theme.colors.warning} />} iconBg={`${theme.colors.warning}08`} label="Closing Date" value={deal.closing_date ? fmtDate(deal.closing_date) : '—'}
            extra={daysToClose != null ? (daysToClose > 0 ? `${daysToClose}d away` : daysToClose === 0 ? 'Today' : `${Math.abs(daysToClose)}d ago`) : null}
            extraColor={daysToClose != null ? (daysToClose < 0 ? theme.colors.danger : daysToClose <= 7 ? theme.colors.warning : theme.colors.success) : null}
          />
          <SummaryItem icon={<Briefcase size={13} color="#6366F1" />} iconBg="#6366F108" label="Deal Type" value={dealTypeLabel || '—'} />
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8, flexShrink: 0, marginTop: 1,
              background: `${stat.color}10`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><Flag size={13} color={stat.color} /></div>
            <div>
              <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</div>
              <StatusBadge stat={stat} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Key Dates Timeline Card ───────────────────────────── */}
      <div style={{
        padding: 16, borderRadius: 12,
        border: `1px solid ${theme.colors.border}`,
        background: theme.colors.card,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Clock size={14} color={theme.colors.warning} />
            <span style={{ fontSize: 11, fontWeight: 700, color: theme.colors.text, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Key Dates</span>
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, color: progressPct === 100 ? theme.colors.success : theme.colors.primary }}>{progressPct}% complete</span>
        </div>

        {/* Progress bar */}
        <div style={{ height: 6, borderRadius: 3, background: theme.colors.border, marginBottom: 16, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 3,
            background: progressPct === 100 ? theme.colors.success : `linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.primaryLight})`,
            width: `${progressPct}%`, transition: 'width 0.5s ease',
          }} />
        </div>

        {/* Vertical timeline */}
        <div style={{ position: 'relative', paddingLeft: 24 }}>
          {/* Vertical connector line */}
          <div style={{ position: 'absolute', left: 7, top: 8, bottom: 8, width: 2, background: theme.colors.border, borderRadius: 1 }} />

          {milestones.map((m, i) => {
            const done = !!deal[m.dateCol] || (currentIdx >= 0 && i < currentIdx);
            const current = i === currentIdx;
            const dateVal = deal[m.dateCol];
            const overdue = !done && isOverdue(dateVal, deal.status);
            return (
              <div key={m.key} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                marginBottom: i < milestones.length - 1 ? 10 : 0,
                position: 'relative', minHeight: 28,
              }}>
                {/* Timeline dot */}
                <div style={{
                  width: 16, height: 16, borderRadius: 8, flexShrink: 0,
                  background: done ? theme.colors.success : current ? theme.colors.primary : theme.colors.card,
                  border: `2.5px solid ${done ? theme.colors.success : current ? theme.colors.primary : overdue ? theme.colors.danger : theme.colors.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 1, transition: 'all 0.2s ease',
                }}>
                  {done && <Check size={8} color="white" strokeWidth={3} />}
                  {current && <div style={{ width: 5, height: 5, borderRadius: 3, background: 'white' }} />}
                </div>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 0 }}>
                  <span style={{
                    fontSize: 12.5,
                    fontWeight: current ? 700 : done ? 500 : 500,
                    color: done ? theme.colors.textLight : current ? theme.colors.primary : theme.colors.text,
                    textDecoration: done ? 'none' : 'none',
                  }}>{m.label}</span>
                  <span style={{
                    fontSize: 11, flexShrink: 0, marginLeft: 8,
                    fontWeight: overdue ? 700 : 500,
                    color: done ? theme.colors.success : overdue ? theme.colors.danger : theme.colors.textLight,
                  }}>
                    {dateVal ? fmtDate(dateVal) : '—'}
                    {overdue && ' (overdue)'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Financials Card ───────────────────────────────────── */}
      <div style={{
        padding: 16, borderRadius: 12,
        background: `linear-gradient(135deg, ${theme.colors.success}05, ${theme.colors.success}02)`,
        border: `1px solid ${theme.colors.success}18`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
          <DollarSign size={14} color={theme.colors.success} />
          <span style={{ fontSize: 11, fontWeight: 700, color: theme.colors.text, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Financials</span>
        </div>

        {isCommercialDeal(deal) && deal.deal_type === 'commercial_lease' ? (
          /* ── Commercial Lease Financials ── */
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <MoneyField deal={deal} onUpdate={onUpdate} field="lease_rate_sf_year" label="Lease rate ($/SF/yr)" />
              <NumberField deal={deal} onUpdate={onUpdate} field="leasable_sf" label="Leasable SF" suffix="SF" />
              <NumberField deal={deal} onUpdate={onUpdate} field="lease_term_months" label="Lease term" suffix="months" />
              <div style={{ marginBottom: 4 }}>
                <label style={{ fontSize: 10, fontWeight: 600, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Lease type</label>
                <select value={deal.lease_type || ''} onChange={e => onUpdate({ lease_type: e.target.value || null })}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${theme.colors.border}`, fontSize: 12, marginTop: 3, background: theme.colors.card }}>
                  <option value="">Select...</option>
                  <option value="nnn">NNN (Triple Net)</option>
                  <option value="gross">Gross</option>
                  <option value="modified_gross">Modified Gross</option>
                  <option value="ground">Ground Lease</option>
                </select>
              </div>
              <MoneyField deal={deal} onUpdate={onUpdate} field="cam_psf" label="CAM ($/SF/yr)" />
              <MoneyField deal={deal} onUpdate={onUpdate} field="ti_allowance_psf" label="TI allowance ($/SF)" />
              <NumberField deal={deal} onUpdate={onUpdate} field="free_rent_months" label="Free rent" suffix="months" />
              <NumberField deal={deal} onUpdate={onUpdate} field="rent_escalation_pct" label="Annual escalation" suffix="%" />
            </div>

            {/* Total Lease Value + Commission calc */}
            {(() => {
              const rate = Number(deal.lease_rate_sf_year || 0);
              const sf = Number(deal.leasable_sf || 0);
              const termYears = Number(deal.lease_term_months || 0) / 12;
              const totalLeaseValue = rate * sf * termYears;
              const commPct = Number(deal.commission_pct || 0);
              const commAmt = totalLeaseValue > 0 && commPct > 0 ? Math.round(totalLeaseValue * commPct / 100 * 100) / 100 : 0;
              const agentSplit = Number(deal.agent_split_pct || 0);
              const agentPay = agentSplit > 0 && commAmt > 0 ? Math.round(commAmt * agentSplit / 100 * 100) / 100 : 0;

              return totalLeaseValue > 0 ? (
                <div style={{ marginTop: 12 }}>
                  <div style={{
                    padding: '10px 14px', borderRadius: 8, marginBottom: 8,
                    background: `${theme.colors.primary}08`, border: `1px solid ${theme.colors.primary}15`,
                  }}>
                    <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Lease Value</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: theme.colors.text, marginTop: 2 }}>{fmtMoney(totalLeaseValue)}</div>
                    <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 2 }}>
                      ${rate.toFixed(2)}/SF × {sf.toLocaleString()} SF × {termYears.toFixed(1)} yr
                    </div>
                  </div>
                  <NumberField deal={deal} onUpdate={(patch) => {
                    const pct = patch.commission_pct;
                    if (pct != null && totalLeaseValue > 0) {
                      const ca = Math.round(totalLeaseValue * pct / 100 * 100) / 100;
                      const ap = agentSplit > 0 ? Math.round(ca * agentSplit / 100 * 100) / 100 : 0;
                      onUpdate({
                        commission_pct: pct,
                        commission_amount: ca,
                        total_lease_value: Math.round(totalLeaseValue * 100) / 100,
                        agent_payout: ap,
                        brokerage_payout: Math.round((ca - ap) * 100) / 100,
                      });
                    } else {
                      onUpdate(patch);
                    }
                  }} field="commission_pct" label="Commission %" suffix="%" />
                  {commAmt > 0 && (
                    <div style={{
                      marginTop: 8, padding: '14px 16px', borderRadius: 10,
                      background: 'rgba(255,255,255,0.7)', border: `1px solid ${theme.colors.border}`,
                      display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12,
                    }}>
                      <div>
                        <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Gross</div>
                        <div style={{ fontSize: 17, fontWeight: 800, color: theme.colors.text, marginTop: 2 }}>{fmtMoney(commAmt)}</div>
                      </div>
                      <div style={{ borderLeft: `1px solid ${theme.colors.border}`, paddingLeft: 12 }}>
                        <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Agent</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.success, marginTop: 2 }}>{fmtMoney(agentPay)}</div>
                      </div>
                      <div style={{ borderLeft: `1px solid ${theme.colors.border}`, paddingLeft: 12 }}>
                        <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Brokerage</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.primary, marginTop: 2 }}>{fmtMoney(commAmt - agentPay)}</div>
                      </div>
                    </div>
                  )}
                </div>
              ) : null;
            })()}
          </>
        ) : (
          /* ── Residential / Commercial Sale Financials (original) ── */
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <MoneyField deal={deal} onUpdate={onUpdate} field="offer_price" label="Offer price" />
              <MoneyField deal={deal} onUpdate={onUpdate} field="sale_price" label="Sale price" />
              <NumberField deal={deal} onUpdate={(patch) => {
                const pct = patch.commission_pct;
                const sp = Number(deal.sale_price || 0);
                const agentSplit = Number(deal.agent_split_pct || 0);
                if (pct != null && sp > 0) {
                  const commAmt = Math.round(sp * pct / 100 * 100) / 100;
                  const agentPay = agentSplit > 0 ? Math.round(commAmt * agentSplit / 100 * 100) / 100 : 0;
                  onUpdate({
                    commission_pct: pct,
                    commission_amount: commAmt,
                    agent_payout: agentPay,
                    brokerage_payout: Math.round((commAmt - agentPay) * 100) / 100,
                  });
                } else {
                  onUpdate(patch);
                }
              }} field="commission_pct" label="Commission %" suffix="%" />
            </div>

            {/* Commission breakdown */}
            {gross > 0 && (
              <div style={{
                marginTop: 12, padding: '14px 16px', borderRadius: 10,
                background: 'rgba(255,255,255,0.7)', border: `1px solid ${theme.colors.border}`,
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12,
              }}>
                <div>
                  <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Gross</div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: theme.colors.text, marginTop: 2 }}>{fmtMoney(gross)}</div>
                </div>
                <div style={{ borderLeft: `1px solid ${theme.colors.border}`, paddingLeft: 12 }}>
                  <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Agent</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.success, marginTop: 2 }}>{fmtMoney(agentPayout)}</div>
                </div>
                <div style={{ borderLeft: `1px solid ${theme.colors.border}`, paddingLeft: 12 }}>
                  <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Brokerage</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.primary, marginTop: 2 }}>{fmtMoney(brokeragePayout)}</div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Status Card ───────────────────────────────────────── */}
      <div style={{
        padding: 16, borderRadius: 12,
        border: `1px solid ${theme.colors.border}`,
        background: theme.colors.card,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <Flag size={14} color={theme.colors.textLight} />
          <span style={{ fontSize: 11, fontWeight: 700, color: theme.colors.text, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Update Status</span>
        </div>
        <select
          value={deal.status}
          onChange={e => { onUpdate({ status: e.target.value }); if (onStatusChange) onStatusChange(e.target.value); }}
          style={{ ...fieldInput(), width: '100%' }}
        >
          {Object.entries(STATUS_META).map(([v, m]) => (
            <option key={v} value={v}>{m.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

/* Summary item for Deal Summary card */
function SummaryItem({ icon, iconBg, label, value, bold, extra, extraColor }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8, flexShrink: 0, marginTop: 1,
        background: iconBg || '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{icon}</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <div style={{
          fontSize: 13, fontWeight: bold ? 700 : 600, color: theme.colors.text, marginTop: 1,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{value}</div>
        {extra && <div style={{ fontSize: 10, fontWeight: 600, color: extraColor || theme.colors.textLight, marginTop: 1 }}>{extra}</div>}
      </div>
    </div>
  );
}

function MilestonesTab({ deal, onAdvance }) {
  const [promptOpen, setPromptOpen] = useState(null);
  const [note, setNote] = useState('');
  const [milestoneStatus, setMilestoneStatus] = useState({});
  const [loadingStatus, setLoadingStatus] = useState(true);
  const milestones = getMilestoneFlow(deal);
  const currentIdx = milestones.findIndex(m => m.key === deal.status);
  const commercial = isCommercialDeal(deal);

  // Fetch evidence-based milestone status
  const loadStatus = useCallback(async () => {
    if (!deal?.id) return;
    setLoadingStatus(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/deal-verify?deal_id=${deal.id}&action=milestone_status`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (r.ok) setMilestoneStatus(data.milestones || {});
    } catch { /* noop */ }
    finally { setLoadingStatus(false); }
  }, [deal?.id]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  return (
    <div style={{ padding: 18 }}>
      {commercial && (
        <div style={{
          padding: '8px 12px', marginBottom: 12, borderRadius: 8,
          background: '#EDE9FE', fontSize: 11, fontWeight: 600, color: '#7C3AED',
        }}>
          Commercial deal — milestones follow the LOI → Due Diligence → Purchase Agreement → Estoppels/SNDA → Financing pipeline
        </div>
      )}
      <div style={{ fontSize: 11, color: theme.colors.textLight, marginBottom: 12, lineHeight: 1.5 }}>
        Milestones complete automatically when required documents are uploaded, verified by both agent and broker, and signed.
        Each step is tracked in the Traceability tab for full audit compliance.
      </div>
      {milestones.map((m, i) => {
        const doneDate = deal[m.dateCol];
        const done = !!doneDate || (currentIdx >= 0 && i < currentIdx);
        const next = !done && (i === currentIdx || (currentIdx < 0 && i === 0));
        const evidence = milestoneStatus[m.key];
        const completable = evidence?.completable;
        const progress = evidence?.progress;
        const requirements = evidence?.requirements || [];

        return (
          <div key={m.key} style={{
            padding: '12px 14px', marginBottom: 8, borderRadius: 10,
            border: `1px solid ${done ? theme.colors.success + '40' : next ? theme.colors.accent + '40' : theme.colors.border}`,
            background: done ? '#F0FDF4' : next ? '#FFF7ED' : theme.colors.card,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 14, flexShrink: 0,
                background: done ? theme.colors.success : next ? theme.colors.accent : '#E5E7EB',
                color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 800,
              }}>
                {done ? <Check size={14} /> : i + 1}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>{m.label}</div>
                <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 2 }}>
                  {doneDate ? `Completed ${fmtDate(doneDate)}` : done ? 'Completed' : next ? 'Up next' : 'Pending'}
                </div>
              </div>
              {!done && (
                <button
                  onClick={() => { if (completable) { setPromptOpen(m.key); setNote(''); } }}
                  disabled={!completable}
                  title={completable ? 'All evidence verified — ready to complete' : 'Required documents must be uploaded, verified, and signed first'}
                  style={{
                    padding: '6px 10px', borderRadius: 8, border: 'none',
                    background: completable ? theme.colors.success : '#E5E7EB',
                    color: completable ? 'white' : '#9CA3AF',
                    fontSize: 11, fontWeight: 700,
                    cursor: completable ? 'pointer' : 'not-allowed',
                    opacity: completable ? 1 : 0.7,
                  }}>{completable ? 'Complete' : 'Locked'}</button>
              )}
            </div>

            {/* Evidence requirements — what's needed to unlock this milestone */}
            {!done && requirements.length > 0 && (
              <div style={{ marginTop: 8, marginLeft: 38 }}>
                {requirements.map(req => (
                  <div key={req.key} style={{
                    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3,
                    fontSize: 10, color: req.complete ? theme.colors.success : theme.colors.textLight,
                  }}>
                    <div style={{
                      width: 12, height: 12, borderRadius: 3, flexShrink: 0,
                      background: req.complete ? theme.colors.success : 'transparent',
                      border: `1.5px solid ${req.complete ? theme.colors.success : theme.colors.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {req.complete && <Check size={7} color="white" strokeWidth={3} />}
                    </div>
                    <span style={{ textDecoration: req.complete ? 'line-through' : 'none' }}>
                      {req.key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                    </span>
                    {!req.complete && (
                      <span style={{ color: theme.colors.textLight, fontSize: 9 }}>
                        {!req.uploaded ? '— not uploaded' : !req.agentVerified ? '— needs agent review' : !req.brokerVerified ? '— needs broker verification' : '— needs signature'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {promptOpen && (
        <div onClick={() => setPromptOpen(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 14,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: theme.colors.card, borderRadius: 12, padding: 18, width: '100%', maxWidth: 360,
          }}>
            <h3 style={{ margin: 0, fontSize: 15, color: theme.colors.text, marginBottom: 8 }}>
              Mark "{milestones.find(x => x.key === promptOpen)?.label}" done?
            </h3>
            <textarea value={note} onChange={e => setNote(e.target.value)}
              placeholder="Optional note (visible on deal timeline)"
              rows={3} style={{ ...fieldInput(), width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
            />
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
              <button onClick={() => setPromptOpen(null)} style={ghostBtn()}>Cancel</button>
              <button onClick={() => { onAdvance(promptOpen, note.trim() || null); setPromptOpen(null); }} style={primaryBtn()}>
                <Check size={13} /> Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PartiesTab({ deal, onUpdate }) {
  // Every party gets consistent fields: Company, Contact Name, Phone, Email
  const PARTY_CONFIG = [
    {
      title: 'Co-op / Listing Agent',
      icon: User,
      color: '#8B5CF6',
      nameField: 'other_agent_name',
      fields: [
        { field: 'other_brokerage', label: 'Company', icon: Building2 },
        { field: 'other_agent_phone', label: 'Phone', icon: Phone },
        { field: 'other_agent_email', label: 'Email', icon: Mail },
      ],
    },
    {
      title: 'Attorney',
      icon: Scale,
      color: '#2563EB',
      nameField: 'attorney_name',
      fields: [
        { field: 'attorney_phone', label: 'Phone', icon: Phone },
        { field: 'attorney_email', label: 'Email', icon: Mail },
      ],
    },
    {
      title: 'Lender',
      icon: Landmark,
      color: '#0EA5E9',
      nameField: 'lender_name',
      fields: [
        { field: 'lender_contact', label: 'Contact name', icon: User },
        { field: 'lender_phone', label: 'Phone', icon: Phone },
        { field: 'lender_email', label: 'Email', icon: Mail },
      ],
    },
    {
      title: 'Title Company',
      icon: Shield,
      color: '#F59E0B',
      nameField: 'title_company',
      fields: [
        { field: 'title_contact', label: 'Contact name', icon: User },
        { field: 'title_phone', label: 'Phone', icon: Phone },
        { field: 'title_email', label: 'Email', icon: Mail },
      ],
    },
    {
      title: 'Inspector',
      icon: ClipboardList,
      color: '#10B981',
      nameField: 'inspector_name',
      fields: [
        { field: 'inspector_phone', label: 'Phone', icon: Phone },
        { field: 'inspector_email', label: 'Email', icon: Mail },
      ],
    },
  ];

  return (
    <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 2 }}>
        Click any field to edit. All changes save automatically.
      </div>
      {PARTY_CONFIG.map(party => (
        <PartyCard key={party.title} deal={deal} onUpdate={onUpdate} party={party} />
      ))}
    </div>
  );
}

function PartyCard({ deal, onUpdate, party }) {
  const { title, icon: Icon, color, nameField, fields } = party;
  const name = deal[nameField] || '';
  const hasAnyData = name || fields.some(f => deal[f.field]);

  // Generate initials from name
  const initials = name
    ? name.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : null;

  return (
    <div style={{
      borderRadius: 12, overflow: 'hidden',
      border: `1px solid ${hasAnyData ? color + '25' : theme.colors.border}`,
      background: theme.colors.card,
      transition: 'border-color 0.2s',
    }}>
      {/* Card header with avatar */}
      <div style={{
        padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12,
        background: hasAnyData ? `${color}04` : 'transparent',
        borderBottom: `1px solid ${theme.colors.border}`,
      }}>
        {/* Avatar */}
        <div style={{
          width: 40, height: 40, borderRadius: 12, flexShrink: 0,
          background: hasAnyData ? `linear-gradient(135deg, ${color}, ${color}CC)` : `${color}12`,
          color: hasAnyData ? 'white' : color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: initials ? 14 : 16, fontWeight: 700,
          transition: 'all 0.2s',
        }}>
          {initials || <Icon size={18} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</div>
          <EditableField deal={deal} field={nameField} label="" onUpdate={onUpdate} inline />
        </div>
      </div>

      {/* Fields */}
      <div style={{ padding: '10px 16px 12px' }}>
        {fields.map(f => {
          const FieldIcon = f.icon;
          return (
            <div key={f.field} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <FieldIcon size={12} color={theme.colors.textLight} style={{ flexShrink: 0, marginTop: 1 }} />
              <EditableField deal={deal} field={f.field} label={f.label} onUpdate={onUpdate} compact />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CommissionTab({ deal, onUpdate }) {
  const salePrice = Number(deal.sale_price || 0);
  const commPct = Number(deal.commission_pct || 0);
  const gross = Number(deal.commission_amount || 0);
  const agentPct = Number(deal.agent_split_pct || 0);
  const agentPayout = Number(deal.agent_payout || 0);
  const brokerage = Number(deal.brokerage_payout || 0);

  // Recalculate everything when agent split % changes
  const handleSplitChange = (patch) => {
    const newPct = patch.agent_split_pct;
    if (newPct != null && gross > 0) {
      const ap = Math.round(gross * newPct / 100 * 100) / 100;
      onUpdate({
        agent_split_pct: newPct,
        agent_payout: ap,
        brokerage_payout: Math.round((gross - ap) * 100) / 100,
      });
    } else {
      onUpdate(patch);
    }
  };

  return (
    <div style={{ padding: 18 }}>
      <Section title="Commission breakdown">
        <div style={fieldRow()}>
          <div style={fieldLabel()}>Sale price</div>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: theme.colors.text }}>
            {salePrice > 0 ? fmtMoney(salePrice) : <span style={{ color: theme.colors.textLight }}>— set in Overview</span>}
          </div>
        </div>
        <div style={fieldRow()}>
          <div style={fieldLabel()}>Commission %</div>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: theme.colors.text }}>
            {commPct > 0 ? `${commPct}%` : <span style={{ color: theme.colors.textLight }}>— set in Overview</span>}
          </div>
        </div>
        <PayoutRow label="Gross commission" value={gross} color={theme.colors.text} big />
      </Section>

      <Section title="Agent split">
        <div style={{ fontSize: 11, color: theme.colors.textLight, marginBottom: 8 }}>
          Agent split is pre-filled from the agent's profile. Override below for this deal only.
        </div>
        <NumberField deal={deal} onUpdate={handleSplitChange} field="agent_split_pct" label="Agent split %" suffix="%" />
      </Section>

      <Section title="Payout">
        <PayoutRow label="Agent payout"     value={agentPayout}   color={theme.colors.success} />
        <PayoutRow label="Brokerage payout" value={brokerage}     color={theme.colors.primary} />
      </Section>

      {salePrice === 0 && (
        <div style={{ padding: 10, background: '#FEF3C7', borderRadius: 8, fontSize: 11, color: '#92400E', marginTop: 8 }}>
          Enter the Sale Price and Commission % in the Overview tab to see the breakdown here.
        </div>
      )}
    </div>
  );
}

function TasksTab({ deal, showToast }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [newDue, setNewDue] = useState(() => new Date(Date.now() + 2 * 86400000).toISOString().split('T')[0]);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!deal?.contact_id) { setTasks([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/tasks?range=all`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (r.ok) setTasks((data.tasks || []).filter(t => t.lead_id === deal.contact_id));
    } catch { /* noop */ } finally { setLoading(false); }
  }, [deal?.contact_id]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!newTitle.trim() || !deal.contact_id) return;
    setAdding(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/tasks', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTitle.trim(), due_date: newDue, lead_id: deal.contact_id, agent_id: deal.agent_id }),
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Failed');
      setNewTitle(''); showToast?.('Task added'); load();
    } catch (e) { showToast?.(e.message); }
    finally { setAdding(false); }
  };

  if (!deal.contact_id) {
    return <Center>Link a contact to this deal to track tasks.</Center>;
  }

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        <input value={newTitle} onChange={e => setNewTitle(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') add(); }}
          placeholder="Add a task for this deal…"
          style={{ ...fieldInput(), flex: 1 }}
        />
        <input type="date" value={newDue} onChange={e => setNewDue(e.target.value)} style={{ ...fieldInput(), width: 140 }} />
        <button onClick={add} disabled={adding || !newTitle.trim()} style={{
          padding: '9px 14px', borderRadius: 8, border: 'none',
          background: theme.colors.primary, color: 'white', fontWeight: 600, fontSize: 12, cursor: adding ? 'wait' : 'pointer',
          opacity: (adding || !newTitle.trim()) ? 0.5 : 1,
        }}><Plus size={14} /></button>
      </div>
      {loading ? <Center><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</Center>
        : !tasks.length ? <Center>No tasks yet.</Center>
        : tasks.map(t => (
            <div key={t.id} style={{
              padding: '10px 0', borderBottom: `1px solid ${theme.colors.border}`,
              display: 'flex', alignItems: 'center', gap: 10,
              opacity: t.status === 'completed' ? 0.55 : 1,
            }}>
              <div style={{ fontSize: 13, color: theme.colors.text, fontWeight: 600, textDecoration: t.status === 'completed' ? 'line-through' : 'none' }}>
                {t.title}
              </div>
              <div style={{ marginLeft: 'auto', fontSize: 11, color: theme.colors.textLight }}>Due {t.due_date}</div>
            </div>
          ))}
    </div>
  );
}

function NotesTab({ dealId, showToast }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/deal-notes?deal_id=${dealId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (r.ok) setNotes(data.notes || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/deal-notes', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ deal_id: dealId, body: text.trim() }),
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Failed');
      setText(''); load();
    } catch (e) { showToast?.(e.message); }
    finally { setPosting(false); }
  };

  return (
    <div style={{ padding: 18 }}>
      <textarea value={text} onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) post(); }}
        placeholder="Add a note about this deal… ⌘+Enter to post."
        rows={3}
        style={{ ...fieldInput(), width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6, marginBottom: 14 }}>
        <button onClick={post} disabled={posting || !text.trim()} style={{
          padding: '8px 14px', borderRadius: 8, border: 'none',
          background: theme.colors.primary, color: 'white', fontSize: 12, fontWeight: 600,
          cursor: posting ? 'wait' : 'pointer', opacity: (posting || !text.trim()) ? 0.5 : 1,
          display: 'inline-flex', alignItems: 'center', gap: 5,
        }}>
          {posting ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={12} />} Post
        </button>
      </div>
      {loading ? <Center><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</Center>
        : !notes.length ? <Center>No notes yet.</Center>
        : notes.map(n => (
            <div key={n.id} style={{
              padding: '10px 12px', marginBottom: 8, borderRadius: 8,
              background: n.kind === 'milestone' ? '#F0FDF4' : n.kind === 'compliance' ? '#FEF3C7' : '#F8FAFC',
              border: `1px solid ${theme.colors.border}`,
            }}>
              <div style={{ fontSize: 11, color: theme.colors.textLight, fontWeight: 600 }}>
                {n.author_name} · {new Date(n.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                {n.kind !== 'note' && <span> · <em>{n.kind}</em></span>}
              </div>
              <div style={{ fontSize: 13, color: theme.colors.text, marginTop: 5, whiteSpace: 'pre-wrap' }}>{n.body}</div>
            </div>
          ))}
    </div>
  );
}

// ── DocsTab ─────────────────────────────────────────────────────
// Native Trovva document management:
//   - Upload PDFs / other files (stored in Supabase Storage)
//   - Send for e-signature (BoldSign by default; Dropbox Sign as drop-in
//     alternate; env-gated so UI gracefully degrades to a notice when
//     no provider is configured yet.)
//   - Generate from a broker template
//   - Compliance cover sheet
//   - Signed artifacts auto-archive back via the signing webhook
// ── Traceability Tab ─────────────────────────────────────────────
// Complete audit history: who uploaded, reviewed, verified, signed —
// timestamps for everything. Full chain of custody for compliance.
function TraceabilityTab({ dealId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/deal-verify?deal_id=${dealId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (r.ok) setEntries(data.entries || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  const ACTION_META = {
    document_uploaded:    { icon: Upload,      label: 'Uploaded document',       color: theme.colors.primary },
    document_reviewed:    { icon: Eye,         label: 'Reviewed document',       color: '#8B5CF6' },
    document_verified:    { icon: Shield,      label: 'Verified document',       color: theme.colors.success },
    document_rejected:    { icon: AlertCircle, label: 'Rejected document',       color: theme.colors.danger },
    document_requested_changes: { icon: AlertCircle, label: 'Requested changes', color: '#F59E0B' },
    document_signed:      { icon: FileText,    label: 'Document signed',         color: '#10B981' },
    milestone_completed:  { icon: Check,       label: 'Milestone completed',     color: theme.colors.success },
    milestone_reset:      { icon: RotateCcw,   label: 'Milestone reset',         color: theme.colors.warning },
    status_changed:       { icon: Flag,        label: 'Status changed',          color: theme.colors.primary },
    field_updated:        { icon: Briefcase,   label: 'Deal updated',            color: theme.colors.textLight },
  };

  const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'document', label: 'Documents' },
    { key: 'milestone', label: 'Milestones' },
    { key: 'verification', label: 'Verifications' },
  ];

  const filtered = filter === 'all' ? entries : entries.filter(e => {
    if (filter === 'document') return e.target_type === 'document' || e.action?.startsWith('document_');
    if (filter === 'milestone') return e.target_type === 'milestone' || e.action?.startsWith('milestone_');
    if (filter === 'verification') return ['document_verified', 'document_reviewed', 'document_rejected', 'document_requested_changes'].includes(e.action);
    return true;
  });

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <History size={14} color={theme.colors.primary} />
        <span style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text }}>Traceability</span>
        <span style={{ fontSize: 11, color: theme.colors.textLight, marginLeft: 'auto' }}>
          {entries.length} event{entries.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div style={{ fontSize: 11, color: theme.colors.textLight, marginBottom: 12, lineHeight: 1.5 }}>
        Complete audit trail — every document upload, review, verification, signature, and milestone action with who did it and when.
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
            fontSize: 11, fontWeight: 600,
            background: filter === f.key ? theme.colors.primary : `${theme.colors.primary}08`,
            color: filter === f.key ? 'white' : theme.colors.primary,
          }}>{f.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textLight, fontSize: 13 }}>
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', marginRight: 6 }} /> Loading audit trail…
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textLight, fontSize: 13 }}>
          <History size={24} style={{ marginBottom: 8, opacity: 0.3 }} />
          <div>No events recorded yet.</div>
          <div style={{ fontSize: 11, marginTop: 4 }}>Actions will appear here as documents are uploaded, reviewed, and verified.</div>
        </div>
      ) : (
        <div style={{ position: 'relative', paddingLeft: 24 }}>
          {/* Vertical timeline line */}
          <div style={{ position: 'absolute', left: 7, top: 8, bottom: 8, width: 2, background: theme.colors.border, borderRadius: 1 }} />

          {filtered.map((entry, i) => {
            const meta = ACTION_META[entry.action] || { icon: Clock, label: entry.action, color: theme.colors.textLight };
            const Icon = meta.icon;
            const ts = new Date(entry.created_at);
            const details = entry.details || {};

            return (
              <div key={entry.id || i} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                marginBottom: 10, position: 'relative',
              }}>
                {/* Timeline dot */}
                <div style={{
                  width: 16, height: 16, borderRadius: 8, flexShrink: 0,
                  background: `${meta.color}15`, border: `2px solid ${meta.color}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 1,
                }}>
                  <Icon size={8} color={meta.color} />
                </div>

                <div style={{
                  flex: 1, padding: '8px 12px', borderRadius: 8,
                  background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text }}>
                        {entry.actor_name || 'System'}
                      </span>
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 4, marginLeft: 5,
                        background: entry.actor_role === 'broker' ? '#8B5CF612' : `${theme.colors.primary}08`,
                        color: entry.actor_role === 'broker' ? '#8B5CF6' : theme.colors.primary,
                        textTransform: 'uppercase',
                      }}>{entry.actor_role}</span>
                    </div>
                    <span style={{ fontSize: 10, color: theme.colors.textLight, flexShrink: 0 }}>
                      {ts.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </span>
                  </div>

                  <div style={{ fontSize: 12, color: theme.colors.text, marginTop: 3 }}>
                    <span style={{ color: meta.color, fontWeight: 600 }}>{meta.label}</span>
                    {entry.target_name && <span style={{ color: theme.colors.textLight }}> — {entry.target_name}</span>}
                  </div>

                  {details.notes && (
                    <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 4, fontStyle: 'italic' }}>
                      "{details.notes}"
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DocsTab({ deal, showToast }) {
  const [docs, setDocs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState({ provider: 'manual', configured: false });
  const [uploading, setUploading] = useState(false);
  const [signDoc, setSignDoc] = useState(null);        // legacy single-doc (null when using bundle)
  const [bundleDocs, setBundleDocs] = useState(null);  // array of docs for multi-doc envelope
  const [selectedDocIds, setSelectedDocIds] = useState(new Set()); // checkbox selection for bundling
  const [envelopeBalance, setEnvelopeBalance] = useState(null);
  const [showBuyPack, setShowBuyPack] = useState(false);
  const [clientPortalExpanded, setClientPortalExpanded] = useState(false);
  const [formFillerDoc, setFormFillerDoc] = useState(null);
  const [templatePicker, setTemplatePicker] = useState(null); // { checklistKey, templates[] }
  const [selectedTemplateIds, setSelectedTemplateIds] = useState(new Set()); // template browser selection

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const [rDocs, rTpl, rBal] = await Promise.all([
        fetch(`/api/deal-documents?deal_id=${deal.id}`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }),
        fetch('/api/deal-templates', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }),
        fetch('/api/envelope-balance', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }),
      ]);
      const jDocs = await rDocs.json();
      const jTpl  = await rTpl.json();
      const jBal  = await rBal.json();
      if (rDocs.ok) { setDocs(jDocs.documents || []); setSigning(jDocs.signing || { provider: 'manual', configured: false }); }
      if (rTpl.ok) setTemplates(jTpl.templates || []);
      if (rBal.ok) setEnvelopeBalance(jBal);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [deal.id]);
  useEffect(() => { load(); }, [load]);

  // Document selection helpers for bundling
  const verifiedDocs = docs.filter(d =>
    (d.signing_status === 'unsigned' || !d.signing_status) &&
    (d.verification_status === 'verified' || (d.agent_verified_at && d.broker_verified_at)) &&
    !d.parent_document_id  // exclude signed copies
  );
  const toggleDocSelection = (docId) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  };
  const selectedDocs = docs.filter(d => selectedDocIds.has(d.id));
  const openBundle = () => {
    if (selectedDocs.length > 0) {
      setBundleDocs(selectedDocs);
      setSelectedDocIds(new Set());
    }
  };

  const buyPack = async (quantity = 10) => {
    const { data: { session } } = await supabase.auth.getSession();
    const r = await fetch('/api/envelope-balance?action=buy_pack', {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ quantity }),
    });
    const j = await r.json();
    if (!r.ok) { alert(`Could not add pack: ${j.error}`); return; }
    setShowBuyPack(false);
    load();
  };

  const onUploadFile = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { alert('File too large (25 MB max)'); return; }
    setUploading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const base64 = await fileToBase64(file);
      const r = await fetch('/api/deal-documents', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ deal_id: deal.id, name: file.name, mime: file.type, base64 }),
      });
      if (!r.ok) { const j = await r.json(); alert(`Upload failed: ${j.error}`); }
      else await load();
    } finally { setUploading(false); }
  };

  const onDownload = async (doc) => {
    const { data: { session } } = await supabase.auth.getSession();
    const r = await fetch(`/api/deal-documents?id=${doc.id}&download=1`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    const j = await r.json();
    if (r.ok && j.url) window.open(j.url, '_blank', 'noopener');
    else alert(`Download failed: ${j.error || 'unknown'}`);
  };

  const onDelete = async (doc) => {
    if (!confirm(`Delete "${doc.name}"? This can't be undone.`)) return;
    const { data: { session } } = await supabase.auth.getSession();
    const r = await fetch(`/api/deal-documents?id=${doc.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (r.ok) load(); else { const j = await r.json(); alert(`Delete failed: ${j.error}`); }
  };

  // Generate a document from a template — returns the doc object, does NOT call load()
  const onInstantiate = async (tplId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/deal-templates?action=instantiate', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_id: tplId, deal_id: deal.id }),
      });
      if (r.ok) {
        const data = await r.json();
        return data.document;
      } else {
        const j = await r.json();
        showToast?.(`Generate failed: ${j.error}`);
        return null;
      }
    } catch (e) {
      showToast?.(`Generate error: ${e.message}`);
      return null;
    }
  };

  // Generate from template + immediately open in PDF Form Filler
  const generateAndOpen = async (tplId) => {
    const tpl = templates.find(t => t.id === tplId);
    if (tpl) {
      const existing = docs.find(d => d.template_id === tpl.id);
      if (existing) { setFormFillerDoc(existing); return; }
    }
    showToast?.('Generating and auto-filling…');
    const doc = await onInstantiate(tplId);
    if (doc) {
      await load(); // Refresh doc list
      setFormFillerDoc(doc); // Open in PdfFormFiller — doc has id from API
    }
  };

  const printComplianceSheet = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const url = `/api/deal-compliance-pdf?deal_id=${deal.id}&token=${encodeURIComponent(session.access_token)}`;
    window.open(url, '_blank', 'noopener');
  };

  // Group templates by category
  const groupedTemplates = useCallback(() => {
    const groups = {
      'Contract & Addenda': [],
      'Agency & Consent': [],
      'Disclosures': [],
      'Lease': [],
      'Listing': [],
    };
    templates.forEach(t => {
      if (t.kind === 'contract') groups['Contract & Addenda'].push(t);
      else if (t.kind === 'agency_disclosure') groups['Agency & Consent'].push(t);
      else if (['disclosure', 'lead_paint', 'wire_fraud', 'flood_risk', 'opra_notice'].includes(t.kind)) groups['Disclosures'].push(t);
      else if (t.kind === 'lease') groups['Lease'].push(t);
      else if (t.kind === 'listing_agreement') groups['Listing'].push(t);
    });
    return groups;
  }, [templates]);

  // Smart suggestions based on deal type
  const getSmartSuggestions = useCallback(() => {
    const suggestions = new Set();
    if (deal.deal_type === 'residential_sale') {
      const kinds = ['contract', 'disclosure', 'lead_paint', 'agency_disclosure', 'wire_fraud'];
      kinds.forEach(k => {
        const tpl = templates.find(t => t.kind === k);
        if (tpl) suggestions.add(tpl.id);
      });
    }
    return suggestions;
  }, [deal.deal_type, templates]);

  // Initialize selected templates with smart suggestions on first load
  useEffect(() => {
    if (templates.length > 0 && selectedTemplateIds.size === 0) {
      setSelectedTemplateIds(getSmartSuggestions());
    }
  }, [templates, selectedTemplateIds.size, getSmartSuggestions]);

  // Match brokerage templates to a checklist item — returns ALL matching templates
  const matchTemplatesToChecklist = useCallback((checklistItem) => {
    if (!templates.length) return [];
    return templates.filter(t => t.kind === checklistItem.key);
  }, [templates]);

  // Single-template convenience (for backwards compat in status logic)
  const matchTemplateToChecklist = useCallback((checklistItem) => {
    const matches = matchTemplatesToChecklist(checklistItem);
    return matches.length > 0 ? matches[0] : null;
  }, [matchTemplatesToChecklist]);


  // Count docs assigned to current user
  const myPendingDocs = docs.filter(d => d.assigned_to && d.next_action !== 'complete');
  const pendingForMe = myPendingDocs.length;

  return (
    <div style={{ padding: 18 }}>
      {/* ── Action needed banner ───────────────────────────── */}
      {pendingForMe > 0 && (
        <div style={{
          padding: '10px 14px', marginBottom: 12, borderRadius: 10,
          background: '#8B5CF608', border: '1px solid #8B5CF625',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
            background: '#8B5CF6', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: 13, fontWeight: 800,
          }}>{pendingForMe}</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>
              {pendingForMe} document{pendingForMe !== 1 ? 's' : ''} need{pendingForMe === 1 ? 's' : ''} your action
            </div>
            <div style={{ fontSize: 11, color: theme.colors.textLight }}>
              Click a document below to review, verify, or send for signature
            </div>
          </div>
        </div>
      )}

      {/* ── Template Browser Card ─────────────────────── */}
      <div style={{
        borderRadius: 12, marginBottom: 14, overflow: 'hidden',
        border: `1px solid ${theme.colors.border}`,
      }}>
        <div style={{
          padding: '14px 16px', background: theme.colors.card,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={16} color={theme.colors.primary} />
            Deal Documents
          </div>
          <button onClick={async () => {
            const selected = Array.from(selectedTemplateIds);
            if (selected.length === 0) { showToast?.('Select at least one template'); return; }
            // Filter out templates already generated for this deal
            const toGenerate = selected.filter(tplId => !docs.find(d => d.template_id === tplId));
            if (toGenerate.length === 0) {
              showToast?.('All selected documents already generated');
              setSelectedTemplateIds(new Set());
              return;
            }
            showToast?.(`Generating ${toGenerate.length} document${toGenerate.length > 1 ? 's' : ''}…`);
            let firstDoc = null;
            for (const tplId of toGenerate) {
              const doc = await onInstantiate(tplId);
              if (doc && !firstDoc) firstDoc = doc;
            }
            setSelectedTemplateIds(new Set());
            await load(); // Refresh doc list once after all generations
            if (firstDoc) {
              showToast?.('Documents generated — opening for review');
              setFormFillerDoc(firstDoc); // Open first doc in PdfFormFiller
            }
          }} style={{
            padding: '6px 12px', borderRadius: 8, border: 'none',
            background: selectedTemplateIds.size > 0 ? theme.colors.primary : theme.colors.border,
            color: selectedTemplateIds.size > 0 ? 'white' : theme.colors.textLight,
            fontSize: 12, fontWeight: 600, cursor: selectedTemplateIds.size > 0 ? 'pointer' : 'default',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            <Plus size={12} /> Generate Selected {selectedTemplateIds.size > 0 ? `(${selectedTemplateIds.size})` : ''}
          </button>
        </div>

        <div style={{ padding: '14px 16px', background: theme.colors.card, borderTop: `1px solid ${theme.colors.border}` }}>
          {loading ? (
            <Center>Loading templates…</Center>
          ) : templates.length === 0 ? (
            <Center><AlertCircle size={14} /> No templates configured for your brokerage</Center>
          ) : (
            Object.entries(groupedTemplates()).map(([category, tpls]) => {
              if (tpls.length === 0) return null;
              return (
                <div key={category} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                    {category} ({tpls.length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {tpls.map(tpl => {
                      const alreadyGenerated = docs.find(d => d.template_id === tpl.id);
                      return (
                        <div key={tpl.id} style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '10px 12px', borderRadius: 8, border: `1px solid ${theme.colors.border}`,
                          background: alreadyGenerated ? '#F0FDF4' : theme.colors.card,
                          cursor: 'pointer', transition: 'background 0.15s',
                        }}
                        onClick={() => {
                          setSelectedTemplateIds(prev => {
                            const next = new Set(prev);
                            if (next.has(tpl.id)) next.delete(tpl.id); else next.add(tpl.id);
                            return next;
                          });
                        }}
                        onMouseEnter={e => { if (!alreadyGenerated) e.currentTarget.style.background = `${theme.colors.primary}06`; }}
                        onMouseLeave={e => { if (!alreadyGenerated) e.currentTarget.style.background = theme.colors.card; }}
                        >
                          <input type="checkbox" checked={selectedTemplateIds.has(tpl.id) || alreadyGenerated}
                            disabled={alreadyGenerated}
                            style={{ width: 18, height: 18, accentColor: theme.colors.primary, cursor: 'pointer' }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>{tpl.name}</div>
                          </div>
                          {alreadyGenerated && (
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4,
                              background: `${theme.colors.success}18`, color: theme.colors.success, textTransform: 'uppercase',
                            }}>Generated</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Compliance cover sheet ──────────────────────────── */}
      <div style={{
        padding: 12, marginBottom: 10,
        background: '#EFF6FF', border: '1px solid #BFDBFE',
        borderRadius: 10, display: 'flex', gap: 10, alignItems: 'center',
      }}>
        <FileText size={16} color="#1D4ED8" />
        <div style={{ flex: 1, fontSize: 12, color: theme.colors.text }}>
          <strong>Compliance cover sheet.</strong>
          <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 2 }}>
            One-click summary of milestones, parties, commission, notes — print or save as PDF for your file.
          </div>
        </div>
        <button onClick={printComplianceSheet} style={{
          padding: '6px 10px', borderRadius: 6, border: 'none',
          background: '#1D4ED8', color: '#fff',
          fontSize: 11, fontWeight: 600, cursor: 'pointer',
        }}>Open</button>
      </div>

      {/* ── Upload ad-hoc document ──────────────────────────── */}
      <div style={{
        display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center',
        padding: '10px 12px', borderRadius: 10, border: `1px dashed ${theme.colors.border}`,
        background: `${theme.colors.primary}03`,
      }}>
        <label style={{
          ...primaryBtn(),
          display: 'inline-flex', gap: 4, alignItems: 'center', cursor: 'pointer',
          opacity: uploading ? 0.6 : 1, flexShrink: 0,
        }}>
          <Upload size={12} /> {uploading ? 'Uploading…' : 'Upload'}
          <input type="file" style={{ display: 'none' }}
            onChange={e => onUploadFile(e.target.files?.[0])}
            accept="application/pdf,image/*,.doc,.docx" />
        </label>
        <div style={{ fontSize: 11, color: theme.colors.textLight, lineHeight: 1.4 }}>
          For supporting documents — inspection reports, pre-approval letters, appraisals, photos, etc.
          <br />NJAR forms are generated from your brokerage templates above.
        </div>
      </div>

      {/* ── Bundle action bar — appears when docs are selected ─── */}
      {selectedDocIds.size > 0 && (
        <div style={{
          padding: '10px 14px', marginBottom: 12, borderRadius: 10,
          background: '#8B5CF608', border: '2px solid #8B5CF640',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
            background: '#8B5CF6', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: 14, fontWeight: 800,
          }}>{selectedDocIds.size}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>
              {selectedDocIds.size} document{selectedDocIds.size !== 1 ? 's' : ''} selected
            </div>
            <div style={{ fontSize: 11, color: theme.colors.textLight }}>
              Bundle into 1 envelope — all signers sign everything in one session
            </div>
          </div>
          <button onClick={() => setSelectedDocIds(new Set())}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: theme.colors.textLight }}>
            <X size={14} />
          </button>
          <button onClick={openBundle}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: '#8B5CF6', color: 'white', fontSize: 13, fontWeight: 700,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
              boxShadow: '0 2px 8px rgba(139,92,246,0.3)',
            }}>
            ✍️ Bundle & Send
          </button>
          <button onClick={async () => {
            try {
              const { data: { session } } = await supabase.auth.getSession();
              const r = await fetch('/api/deal-documents?action=download_bundle', {
                method: 'POST',
                headers: { Authorization: `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ deal_id: deal.id, document_ids: Array.from(selectedDocIds) }),
              });
              if (!r.ok) throw new Error((await r.json()).error);
              const { files } = await r.json();
              // Download each file
              for (const f of files) {
                const a = document.createElement('a');
                a.href = f.url;
                a.download = f.name || 'document.pdf';
                a.target = '_blank';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }
            } catch (e) {
              showToast?.(`Download failed: ${e.message}`);
            }
          }}
            style={{
              padding: '8px 16px', borderRadius: 8,
              border: `1px solid ${theme.colors.border}`,
              background: theme.colors.card, color: theme.colors.text,
              fontSize: 13, fontWeight: 600,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
            📥 Download for DocuSign
          </button>
        </div>
      )}

      {/* ── Envelope meter ─────────────────────────────────── */}
      {envelopeBalance && (
        <EnvelopeMeter balance={envelopeBalance} onBuy={() => setShowBuyPack(true)} />
      )}

      {showBuyPack && (
        <BuyEnvelopePackModal
          balance={envelopeBalance}
          onClose={() => setShowBuyPack(false)}
          onBuy={buyPack}
        />
      )}

      {/* ── Pipeline View ──────────────────────────────────── */}
      {docs.length === 0 ? (
        <div style={{
          padding: 40, textAlign: 'center', color: theme.colors.textLight, fontSize: 13,
          background: theme.colors.card, borderRadius: 10, marginBottom: 14,
        }}>
          <FileText size={20} style={{ marginBottom: 8, opacity: 0.5 }} />
          <div>No documents generated or uploaded yet.</div>
        </div>
      ) : (
        <>
          {['To Review', 'Agent Reviewed', 'Verified', 'Out for Signature', 'Signed'].map(status => {
            // Filter out signed copies (children) from pipeline
            const primaryDocs = docs.filter(d => !(d.parent_document_id && d.source === 'generated' && d.signing_status === 'signed'));
            const statusConfig = {
              'To Review': { emoji: '📝', color: '#8B5CF6', docs: primaryDocs.filter(d => !d.agent_verified_at && d.signing_status !== 'out_for_signature' && d.signing_status !== 'signed') },
              'Agent Reviewed': { emoji: '🔍', color: '#F59E0B', docs: primaryDocs.filter(d => d.agent_verified_at && !d.broker_verified_at && d.signing_status !== 'out_for_signature' && d.signing_status !== 'signed') },
              'Verified': { emoji: '✅', color: '#16A34A', docs: primaryDocs.filter(d => d.agent_verified_at && d.broker_verified_at && d.signing_status !== 'out_for_signature' && d.signing_status !== 'signed') },
              'Out for Signature': { emoji: '✍️', color: '#3B82F6', docs: primaryDocs.filter(d => d.signing_status === 'out_for_signature') },
              'Signed': { emoji: '🎉', color: '#16A34A', docs: primaryDocs.filter(d => d.signing_status === 'signed') },
            };
            const laneDocs = statusConfig[status]?.docs || [];
            if (laneDocs.length === 0) return null;
            return (
              <div key={status} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: theme.colors.textLight, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 14 }}>{statusConfig[status]?.emoji}</span> {status} ({laneDocs.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {laneDocs.map(d => {
                    const signedCopy = docs.find(sc => sc.parent_document_id === d.id && sc.signing_status === 'signed');
                    const agentOk = !!d.agent_verified_at;
                    const brokerOk = !!d.broker_verified_at;
                    const isSigned = d.signing_status === 'signed';
                    const isOut = d.signing_status === 'out_for_signature';
                    const canSelect = agentOk && brokerOk && !isSigned && !isOut;

                    return (
                      <div key={d.id}
                        onClick={() => { if (!isSigned && !isOut) setFormFillerDoc(d); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '10px 12px', borderRadius: 8,
                          border: `1px solid ${theme.colors.border}`,
                          background: theme.colors.card,
                          cursor: isSigned || isOut ? 'default' : 'pointer',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { if (!isSigned && !isOut) e.currentTarget.style.background = `${theme.colors.primary}06`; }}
                        onMouseLeave={e => { e.currentTarget.style.background = theme.colors.card; }}
                      >
                        {/* Selection checkbox for verified docs */}
                        {canSelect && (
                          <input type="checkbox" checked={selectedDocIds.has(d.id)}
                            onClick={e => e.stopPropagation()}
                            onChange={() => toggleDocSelection(d.id)}
                            style={{ width: 16, height: 16, accentColor: '#8B5CF6', cursor: 'pointer', flexShrink: 0 }} />
                        )}
                        <FileText size={14} color={statusConfig[status]?.color || theme.colors.textLight} style={{ flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {d.name}
                          </div>
                          <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {agentOk && <span style={{ color: theme.colors.success }}>Agent ✓</span>}
                            {brokerOk && <span style={{ color: theme.colors.success }}>Broker ✓</span>}
                            {!isSigned && !isOut && !agentOk && <span style={{ color: '#8B5CF6' }}>Click to review</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                          {signedCopy && (
                            <button onClick={() => onDownload(signedCopy)} title="Download signed copy" style={{
                              padding: '5px 8px', borderRadius: 6, border: `1px solid ${theme.colors.border}`,
                              background: '#F0FDF4', cursor: 'pointer', color: theme.colors.success, fontSize: 11, fontWeight: 600,
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                            }}><Check size={11} /> Signed</button>
                          )}
                          {canSelect && (
                            <button onClick={() => setSignDoc(d)} style={{
                              padding: '5px 10px', borderRadius: 6, border: 'none',
                              background: '#8B5CF6', color: 'white', fontSize: 11, fontWeight: 700,
                              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3,
                            }}>✍️ Sign</button>
                          )}
                          {!isSigned && !isOut && (
                            <button onClick={() => setFormFillerDoc(d)} title="Review & fill" style={{
                              padding: '5px 8px', borderRadius: 6, border: `1px solid ${theme.colors.border}`,
                              background: 'transparent', cursor: 'pointer', color: theme.colors.primary,
                              display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600,
                            }}><Eye size={11} /> Review</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* ── Client Portal ─────────────────────────────────── */}
      <div style={{
        borderRadius: 12, marginTop: 16, overflow: 'hidden',
        border: `1px solid ${theme.colors.border}`,
      }}>
        <button onClick={() => setClientPortalExpanded(!clientPortalExpanded)} style={{
          width: '100%', padding: '14px 16px', border: 'none', cursor: 'pointer', textAlign: 'left',
          background: '#F8FAFC',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 30, height: 30, borderRadius: 8,
              background: `${theme.colors.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Send size={14} color={theme.colors.primary} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text }}>Client Portal</div>
              <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 1 }}>
                Share documents, messaging, and deal progress with clients
              </div>
            </div>
          </div>
          <ArrowRight size={14} color={theme.colors.textLight} style={{
            transform: clientPortalExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }} />
        </button>
        {clientPortalExpanded && (
          <div style={{ padding: 16, background: theme.colors.card }}>
            <ClientInvitePanel dealId={deal.id} showToast={showToast} />
            <div style={{ marginTop: 16 }}>
              <DealRoomPanel dealId={deal.id} />
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ────────────────────────────────────────── */}
      {signDoc && (
        <SendForSignatureModal
          doc={signDoc}
          deal={deal}
          allDealDocs={docs}
          envelopeBalance={envelopeBalance}
          onClose={() => setSignDoc(null)}
          onSent={() => { setSignDoc(null); load(); }}
          onBuyPack={() => { setSignDoc(null); setShowBuyPack(true); }}
        />
      )}

      {bundleDocs && (
        <SendForSignatureModal
          docs={bundleDocs}
          deal={deal}
          allDealDocs={docs}
          envelopeBalance={envelopeBalance}
          onClose={() => setBundleDocs(null)}
          onSent={() => { setBundleDocs(null); load(); }}
          onBuyPack={() => { setBundleDocs(null); setShowBuyPack(true); }}
        />
      )}

      {/* Template picker modal — shown when checklist row has multiple NJAR forms */}
      {templatePicker && (
        <div onClick={() => setTemplatePicker(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: theme.colors.card, borderRadius: 14, padding: 22,
            width: '100%', maxWidth: 520, maxHeight: '80vh', overflow: 'hidden',
            boxShadow: '0 30px 80px rgba(0,0,0,0.35)', display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: theme.colors.text }}>
                  {templatePicker.label}
                </div>
                <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 4 }}>
                  {templatePicker.templates.length} NJAR forms available — select one to auto-fill with deal data
                </div>
              </div>
              <button onClick={() => setTemplatePicker(null)} style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: theme.colors.textLight, padding: 4, fontSize: 16,
              }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {templatePicker.templates.map(tpl => {
                // Check if this template was already generated for this deal
                const alreadyGenerated = docs.find(d => d.template_id === tpl.id);
                return (
                  <button key={tpl.id}
                    onClick={() => {
                      setTemplatePicker(null);
                      if (alreadyGenerated) {
                        setFormFillerDoc(alreadyGenerated);
                      } else {
                        generateAndOpen(tpl.id);
                      }
                    }}
                    style={{
                      padding: '12px 14px', borderRadius: 10, border: `1px solid ${theme.colors.border}`,
                      background: alreadyGenerated ? '#F0FDF4' : theme.colors.card,
                      cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12,
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { if (!alreadyGenerated) e.currentTarget.style.background = `${theme.colors.primary}06`; }}
                    onMouseLeave={e => { if (!alreadyGenerated) e.currentTarget.style.background = theme.colors.card; }}
                  >
                    <div style={{
                      width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                      background: alreadyGenerated ? `${theme.colors.success}12` : `${theme.colors.primary}10`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {alreadyGenerated
                        ? <Check size={16} color={theme.colors.success} />
                        : <FileText size={16} color={theme.colors.primary} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>{tpl.name}</div>
                      {tpl.description && (
                        <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 2 }}>{tpl.description}</div>
                      )}
                      {alreadyGenerated && (
                        <div style={{ fontSize: 10, fontWeight: 700, color: theme.colors.success, marginTop: 3, textTransform: 'uppercase' }}>
                          Already generated — click to review
                        </div>
                      )}
                    </div>
                    {!alreadyGenerated && (
                      <span style={{
                        padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                        background: theme.colors.primary, color: 'white', flexShrink: 0,
                      }}>Fill & Review</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {formFillerDoc && (
        <PdfFormFiller
          doc={formFillerDoc}
          deal={deal}
          onClose={() => { setFormFillerDoc(null); load(); }}
          onSent={() => { setFormFillerDoc(null); load(); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

function DocRow({ doc, signing, onDownload, onDelete, onSign, signedCopy, onDownloadSigned }) {
  const status = doc.signing_status || 'unsigned';
  const sigReq = doc.latest_signature_request;
  const signers = Array.isArray(sigReq?.signers) ? sigReq.signers : [];
  const isSignedCopy = doc.source === 'generated' && doc.signing_status === 'signed';

  // E-signature gating: only allow signing after both agent + broker verification
  const isVerified = doc.verification_status === 'verified'
    || (doc.agent_verified_at && doc.broker_verified_at);
  const canSign = status === 'unsigned' && isVerified;
  const needsVerification = status === 'unsigned' && !isVerified;

  const statusMeta = {
    unsigned:            { label: 'Unsigned',          color: '#64748B', icon: '○' },
    out_for_signature:   { label: 'Awaiting signatures', color: '#F59E0B', icon: '⏳' },
    partially_signed:    { label: 'Partially signed',  color: '#F59E0B', icon: '✍️' },
    signed:              { label: 'Fully signed',      color: '#10B981', icon: '✅' },
    declined:            { label: 'Declined',          color: '#DC2626', icon: '❌' },
    cancelled:           { label: 'Cancelled',         color: '#64748B', icon: '⊘' },
    expired:             { label: 'Expired',           color: '#DC2626', icon: '⏰' },
  }[status] || { label: status, color: '#64748B', icon: '○' };

  const SIGNER_STATUS = {
    pending:  { label: 'Awaiting', color: '#94A3B8', bg: '#F1F5F9' },
    viewed:   { label: 'Viewed',   color: '#F59E0B', bg: '#FFFBEB' },
    signed:   { label: 'Signed',   color: '#10B981', bg: '#F0FDF4' },
    declined: { label: 'Declined', color: '#DC2626', bg: '#FEF2F2' },
  };

  return (
    <div style={{
      padding: 12, marginBottom: 8,
      background: isSignedCopy ? '#F0FDF4' : theme.colors.card,
      border: `1px solid ${isSignedCopy ? '#BBF7D0' : status === 'out_for_signature' ? '#FDE68A' : theme.colors.border}`,
      borderRadius: 10,
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 8, flexShrink: 0,
          background: isSignedCopy ? '#10B98118' : `${theme.colors.primary}10`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13,
        }}>
          {isSignedCopy ? '✅' : <FileText size={14} color={theme.colors.primary} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13, fontWeight: 600, color: theme.colors.text,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {isSignedCopy ? `✓ Signed: ${doc.name}` : doc.name}
          </div>
          <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 2 }}>
            {formatDate(doc.created_at)}
            {doc.size_bytes ? ` · ${(doc.size_bytes / 1024).toFixed(0)} KB` : ''}
            {doc.source === 'template' && !isSignedCopy ? ' · auto-generated' : ''}
          </div>
        </div>
        {/* Status badge */}
        <span style={{
          fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
          background: statusMeta.color + '18', color: statusMeta.color,
          textTransform: 'uppercase', letterSpacing: '0.04em',
          whiteSpace: 'nowrap',
        }}>{statusMeta.icon} {statusMeta.label}</span>
      </div>

      {/* Verification gate notice — shown when doc is unsigned but not yet verified */}
      {needsVerification && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, marginTop: 8,
          padding: '6px 10px', borderRadius: 6,
          background: '#FEF3C7', border: '1px solid #FDE68A',
          fontSize: 11, color: '#92400E',
        }}>
          <Shield size={12} />
          <span>
            <strong>Verification required</strong> — Agent review
            {!doc.agent_verified_at ? ' needed' : ' done'}
            {doc.agent_verified_at && !doc.broker_verified_at ? ', broker verification needed' : ''}
            {' '}before sending for signature.
          </span>
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
        <button onClick={onDownload} style={{
          padding: '4px 10px', borderRadius: 6, border: `1px solid ${theme.colors.border}`,
          background: 'transparent', fontSize: 11, fontWeight: 500, cursor: 'pointer',
          color: theme.colors.text, display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <ExternalLink size={11} /> {isSignedCopy ? 'View Signed PDF' : 'Download'}
        </button>
        {canSign && (
          <button onClick={onSign} style={{
            padding: '4px 10px', borderRadius: 6, border: 'none',
            background: '#8B5CF6', color: 'white', fontSize: 11, fontWeight: 600,
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            ✍️ Send for e-Signature
          </button>
        )}
        {/* Show link to signed copy if this is the original and a signed version exists */}
        {signedCopy && (
          <button onClick={onDownloadSigned} style={{
            padding: '4px 10px', borderRadius: 6, border: 'none',
            background: '#10B981', color: 'white', fontSize: 11, fontWeight: 600,
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            ✅ View Signed Copy
          </button>
        )}
        <button onClick={onDelete} title="Delete" style={{
          background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 6px',
          color: theme.colors.textLight, marginLeft: 'auto',
        }}><Trash2 size={12} /></button>
      </div>

      {/* Signer progress — detailed view */}
      {signers.length > 0 && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${theme.colors.border}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            Signers ({signers.filter(s => s.status === 'signed').length}/{signers.length} signed)
          </div>
          {signers.map((s, i) => {
            const sm = SIGNER_STATUS[s.status] || SIGNER_STATUS.pending;
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 8px', marginBottom: 4, borderRadius: 6,
                background: sm.bg,
              }}>
                <span style={{
                  width: 8, height: 8, borderRadius: 4, flexShrink: 0,
                  background: sm.color,
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text }}>{s.name || 'Signer'}</div>
                  <div style={{ fontSize: 10, color: theme.colors.textLight }}>{s.email}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: sm.color }}>{sm.label}</div>
                  {s.signed_at && (
                    <div style={{ fontSize: 9, color: theme.colors.textLight }}>
                      {new Date(s.signed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SendForSignatureModal({ doc, docs: initialDocs, deal, allDealDocs, envelopeBalance, onClose, onSent, onBuyPack }) {
  // Support both single doc (legacy) and multi-doc bundle
  const [envelopeDocs, setEnvelopeDocs] = useState(() => {
    if (initialDocs && initialDocs.length) return initialDocs;
    if (doc) return [doc];
    return [];
  });
  const [showDocPicker, setShowDocPicker] = useState(false);

  const docNames = envelopeDocs.map(d => d.name);
  const defaultSubject = docNames.length === 1
    ? `Signature needed: ${docNames[0]}`
    : `Signature needed: ${docNames.length} documents for your transaction`;
  const defaultMessage = docNames.length === 1
    ? `Please review and sign ${docNames[0]} for your Trovva transaction.`
    : `Please review and sign the following for your Trovva transaction:\n${docNames.map(n => `  - ${n}`).join('\n')}`;

  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState(defaultMessage);

  // Build signers from ALL deal parties with role labels
  const [signers, setSigners] = useState(() => {
    const pre = [];
    const isBuyer = deal.deal_type === 'buyer';

    if (deal.contact?.email) {
      const clientName = `${deal.contact.first_name || ''} ${deal.contact.last_name || ''}`.trim() || 'Client';
      pre.push({ name: clientName, email: deal.contact.email, role: isBuyer ? 'Buyer' : 'Seller' });
    }
    if (deal.other_agent_email) {
      pre.push({ name: deal.other_agent_name || 'Co-op Agent', email: deal.other_agent_email, role: isBuyer ? 'Listing Agent' : "Buyer's Agent" });
    }
    if (deal.attorney_email) {
      pre.push({ name: deal.attorney_name || 'Attorney', email: deal.attorney_email, role: 'Attorney' });
    }

    return pre.length ? pre : [{ name: '', email: '', role: '' }];
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  // Envelope balance
  const remaining = envelopeBalance
    ? Math.max(0, (envelopeBalance.allowance || 0) - (envelopeBalance.used || 0)) + (envelopeBalance.overflow || 0)
    : null;
  const envelopesLow = remaining !== null && remaining <= 3;
  const envelopesOut = remaining !== null && remaining <= 0;

  const addSigner = () => setSigners(s => [...s, { name: '', email: '', role: '' }]);
  const updateSigner = (i, k, v) => setSigners(s => s.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const removeSigner = (i) => setSigners(s => s.filter((_, j) => j !== i));
  const removeDoc = (docId) => setEnvelopeDocs(d => d.filter(x => x.id !== docId));
  const addDoc = (newDoc) => {
    if (!envelopeDocs.some(d => d.id === newDoc.id)) {
      setEnvelopeDocs(d => [...d, newDoc]);
    }
    setShowDocPicker(false);
  };

  // Docs available to add (verified, unsigned, not already in envelope)
  const addableDocs = (allDealDocs || []).filter(d =>
    !envelopeDocs.some(ed => ed.id === d.id) &&
    (d.signing_status === 'unsigned' || !d.signing_status) &&
    (d.verification_status === 'verified' || (d.agent_verified_at && d.broker_verified_at)) &&
    !d.parent_document_id
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!envelopeDocs.length) { setErr('Add at least one document.'); return; }
    if (envelopesOut) { setErr('No envelopes remaining. Buy a booster pack to continue.'); return; }
    const valid = signers.filter(s => (s.name || '').trim() && /\S+@\S+\.\S+/.test(s.email || ''));
    if (!valid.length) { setErr('At least one signer with a valid email is required.'); return; }
    setSubmitting(true); setErr(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/deal-documents?action=request_signature', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_ids: envelopeDocs.map(d => d.id),
          signers: valid.map(s => ({ name: s.name, email: s.email })),
          subject, message,
        }),
      });
      const j = await r.json();
      if (r.status === 402) {
        setErr(`Envelope cap reached. ${j.message || 'Buy a booster pack to continue.'}`);
        return;
      }
      if (!r.ok) throw new Error(j.error || 'Could not send');
      onSent();
    } catch (e2) { setErr(e2.message); }
    finally { setSubmitting(false); }
  };

  const ROLE_COLORS = {
    'Buyer': '#2563EB', 'Seller': '#7C3AED', 'Attorney': '#0F766E',
    'Listing Agent': '#D97706', "Buyer's Agent": '#D97706',
    'Lender': '#0EA5E9', 'Title Company': '#F59E0B',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
      zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
        style={{
          width: '100%', maxWidth: 600, background: '#fff', borderRadius: 12,
          padding: 20, display: 'grid', gap: 10, maxHeight: '90vh', overflow: 'auto',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 700, flex: 1 }}>Send for signature</div>
          {/* Envelope badge */}
          <div style={{
            padding: '4px 10px', borderRadius: 6,
            background: envelopesOut ? '#FEF2F2' : '#F0FDF4',
            border: `1px solid ${envelopesOut ? '#FECACA' : '#BBF7D0'}`,
            fontSize: 11, fontWeight: 700,
            color: envelopesOut ? '#991B1B' : '#065F46',
          }}>
            1 envelope · {envelopeDocs.length} doc{envelopeDocs.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* ── Documents in this envelope ──────────────────── */}
        <div style={{ fontSize: 11, fontWeight: 700, color: theme.colors.textLight,
          textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Documents in this envelope ({envelopeDocs.length})
        </div>
        <div style={{
          borderRadius: 8, border: `1px solid ${theme.colors.border}`,
          overflow: 'hidden',
        }}>
          {envelopeDocs.map((d, i) => (
            <div key={d.id} style={{
              padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 8,
              borderBottom: i < envelopeDocs.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
              background: '#F8FAFC',
            }}>
              <FileText size={13} color={theme.colors.primary} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {d.name}
                </div>
                {d.kind && d.kind !== 'other' && (
                  <span style={{ fontSize: 9, color: theme.colors.textLight }}>{d.kind}</span>
                )}
              </div>
              {envelopeDocs.length > 1 && (
                <button type="button" onClick={() => removeDoc(d.id)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, color: theme.colors.textLight }}>
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
          {/* Add more docs button */}
          {addableDocs.length > 0 && (
            <div style={{ padding: '6px 10px', borderTop: envelopeDocs.length ? `1px solid ${theme.colors.border}` : 'none' }}>
              {!showDocPicker ? (
                <button type="button" onClick={() => setShowDocPicker(true)}
                  style={{ background: 'none', border: 'none', color: theme.colors.primary, cursor: 'pointer',
                    fontSize: 12, fontWeight: 600, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Plus size={12} /> Add more documents to this envelope
                </button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase' }}>
                    Verified documents available
                  </div>
                  {addableDocs.map(ad => (
                    <button key={ad.id} type="button" onClick={() => addDoc(ad)}
                      style={{
                        padding: '6px 8px', borderRadius: 6, border: `1px solid ${theme.colors.border}`,
                        background: '#fff', cursor: 'pointer', textAlign: 'left',
                        display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                      }}>
                      <Plus size={11} color={theme.colors.primary} />
                      <span style={{ fontSize: 12, color: theme.colors.text }}>{ad.name}</span>
                    </button>
                  ))}
                  <button type="button" onClick={() => setShowDocPicker(false)}
                    style={{ background: 'none', border: 'none', color: theme.colors.textLight, cursor: 'pointer', fontSize: 11, padding: 0 }}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Envelope savings nudge */}
        {envelopeDocs.length > 1 && (
          <div style={{
            padding: '6px 10px', borderRadius: 6,
            background: '#F0FDF4', border: '1px solid #BBF7D0',
            fontSize: 11, color: '#065F46', display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Check size={12} color="#10B981" />
            <span>Bundling {envelopeDocs.length} docs saves {envelopeDocs.length - 1} envelope{envelopeDocs.length > 2 ? 's' : ''} — all signers sign everything in one session.</span>
          </div>
        )}

        {/* Envelope warning */}
        {envelopesLow && !envelopesOut && (
          <div style={{
            padding: '8px 12px', borderRadius: 8,
            background: '#FEF3C7', border: '1px solid #FDE68A',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ fontSize: 12, color: '#92400E', flex: 1 }}>
              <strong>Only {remaining} envelope{remaining !== 1 ? 's' : ''} left this month.</strong>
            </span>
            {onBuyPack && (
              <button type="button" onClick={onBuyPack} style={{
                padding: '5px 12px', borderRadius: 6, border: 'none', flexShrink: 0,
                background: '#F59E0B', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer',
              }}>Top up</button>
            )}
          </div>
        )}
        {envelopesOut && (
          <div style={{
            padding: '8px 12px', borderRadius: 8,
            background: '#FEF2F2', border: '1px solid #FECACA',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ fontSize: 12, color: '#991B1B', flex: 1 }}>
              <strong>No envelopes remaining.</strong> Buy a booster pack to send.
            </span>
            {onBuyPack && (
              <button type="button" onClick={onBuyPack} style={{
                padding: '5px 12px', borderRadius: 6, border: 'none', flexShrink: 0,
                background: '#DC2626', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer',
              }}>Buy pack</button>
            )}
          </div>
        )}

        <input value={subject} onChange={e => setSubject(e.target.value)}
          placeholder="Email subject" style={fieldInput()} />
        <textarea value={message} onChange={e => setMessage(e.target.value)}
          placeholder="Message to signers" rows={3}
          style={{ ...fieldInput(), resize: 'vertical', minHeight: 60 }} />

        {/* ── Signers with role labels ────────────────────── */}
        <div style={{ fontSize: 11, fontWeight: 700, color: theme.colors.textLight,
          textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 4 }}>
          Signers (in signing order)
        </div>
        <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: -6 }}>
          Pre-populated from the deal's Parties tab. Remove anyone who doesn't need to sign.
        </div>
        {signers.map((s, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '8px 10px', borderRadius: 8, background: '#F8FAFC', border: `1px solid ${theme.colors.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              {s.role ? (
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                  background: (ROLE_COLORS[s.role] || '#64748B') + '15',
                  color: ROLE_COLORS[s.role] || '#64748B',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>{s.role}</span>
              ) : (
                <span style={{ fontSize: 10, color: theme.colors.textLight, fontStyle: 'italic' }}>Custom signer</span>
              )}
              <span style={{ fontSize: 10, color: theme.colors.textLight, marginLeft: 'auto' }}>#{i + 1}</span>
              <button type="button" onClick={() => removeSigner(i)}
                disabled={signers.length === 1}
                style={{ background: 'transparent', border: 'none', cursor: signers.length === 1 ? 'not-allowed' : 'pointer', color: theme.colors.textLight, padding: 2 }}>
                <Trash2 size={11} />
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 6 }}>
              <input value={s.name} onChange={e => updateSigner(i, 'name', e.target.value)}
                placeholder="Signer name" style={fieldInput()} />
              <input value={s.email} onChange={e => updateSigner(i, 'email', e.target.value)}
                placeholder="signer@example.com" type="email" style={fieldInput()} />
            </div>
          </div>
        ))}
        <button type="button" onClick={addSigner}
          style={{ ...primaryBtn(), background: '#E0E7FF', color: '#3730A3' }}>
          + Add signer
        </button>

        {/* Quick-add from Parties */}
        {(() => {
          const availableParties = [];
          if (deal.lender_email && !signers.some(s => s.email === deal.lender_email)) {
            availableParties.push({ name: deal.lender_name || deal.lender_contact || 'Lender', email: deal.lender_email, role: 'Lender' });
          }
          if (deal.title_email && !signers.some(s => s.email === deal.title_email)) {
            availableParties.push({ name: deal.title_company || deal.title_contact || 'Title Company', email: deal.title_email, role: 'Title Company' });
          }
          if (!availableParties.length) return null;
          return (
            <div style={{ fontSize: 11, color: theme.colors.textLight }}>
              Also in this deal:{' '}
              {availableParties.map((p, i) => (
                <span key={i}>
                  {i > 0 && ', '}
                  <button type="button" onClick={() => setSigners(s => [...s, p])}
                    style={{ background: 'none', border: 'none', color: theme.colors.primary, cursor: 'pointer', fontSize: 11, fontWeight: 600, textDecoration: 'underline', padding: 0 }}>
                    + {p.name} ({p.role})
                  </button>
                </span>
              ))}
            </div>
          );
        })()}

        {err && <div style={{ color: '#B91C1C', fontSize: 12 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
          <button type="button" onClick={onClose}
            style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${theme.colors.border}`, background: '#fff', cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="submit" disabled={submitting || envelopesOut || !envelopeDocs.length} style={{
            ...primaryBtn(),
            opacity: (envelopesOut || !envelopeDocs.length) ? 0.5 : 1,
            cursor: (envelopesOut || !envelopeDocs.length) ? 'not-allowed' : 'pointer',
          }}>
            {submitting ? 'Sending…' : envelopesOut ? 'No envelopes' : `Send ${envelopeDocs.length} doc${envelopeDocs.length !== 1 ? 's' : ''} · 1 envelope`}
          </button>
        </div>
      </form>
    </div>
  );
}

function EnvelopeMeter({ balance, onBuy }) {
  const allowance   = balance.allowance || 0;
  const used        = balance.used || 0;
  const overflow    = balance.overflow || 0;
  const remaining   = Math.max(0, allowance - used) + overflow;
  const pctUsed     = allowance > 0 ? Math.min(100, Math.round((used / allowance) * 100)) : 0;
  const danger      = pctUsed >= 90 || (remaining <= 5 && overflow === 0);
  const warn        = pctUsed >= 75 && !danger;
  const barColor    = danger ? '#DC2626' : warn ? '#F59E0B' : '#10B981';

  return (
    <div style={{
      padding: 10, marginBottom: 12, borderRadius: 8,
      background: '#F8FAFC', border: `1px solid ${theme.colors.border}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Envelopes this month
        </span>
        <span style={{ fontSize: 12, color: theme.colors.text, marginLeft: 'auto' }}>
          <strong>{used}</strong> / {allowance}
          {overflow > 0 && <span style={{ color: '#10B981' }}> + {overflow} pack</span>}
        </span>
      </div>
      <div style={{
        height: 8, borderRadius: 4, background: '#E2E8F0', overflow: 'hidden', marginBottom: 6,
      }}>
        <div style={{
          width: `${pctUsed}%`, height: '100%', background: barColor,
          transition: 'width 200ms ease',
        }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', fontSize: 11, color: theme.colors.textLight }}>
        {remaining} left this cycle
        <button onClick={onBuy} style={{
          marginLeft: 'auto',
          padding: '4px 10px', borderRadius: 6, border: 'none',
          background: danger ? '#DC2626' : theme.colors.primary, color: '#fff',
          fontSize: 11, fontWeight: 600, cursor: 'pointer',
        }}>
          {danger ? 'Buy more' : 'Top up'}
        </button>
      </div>
    </div>
  );
}

function BuyEnvelopePackModal({ balance, onClose, onBuy }) {
  const packs = [
    { qty: 10,  price: 29, label: 'Booster Pack', badge: 'Most popular' },
    { qty: 30,  price: 69, label: 'Team Pack', badge: null },
    { qty: 100, price: 199, label: 'Office Pack', badge: 'Best value' },
  ];
  const [selected, setSelected] = useState(10);
  const [submitting, setSubmitting] = useState(false);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
      zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 12, padding: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Buy envelope pack</div>
        <div style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 12 }}>
          You've used {balance?.used || 0} of {balance?.allowance || 0} this cycle.
          Packs never expire.
        </div>
        <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
          {packs.map(p => {
            const isSel = selected === p.qty;
            return (
              <button key={p.qty} onClick={() => setSelected(p.qty)}
                style={{
                  padding: '12px 14px', borderRadius: 8, textAlign: 'left', cursor: 'pointer',
                  border: isSel ? `2px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`,
                  background: isSel ? '#EFF6FF' : '#fff',
                  display: 'flex', alignItems: 'center', position: 'relative',
                }}>
                {p.badge && (
                  <span style={{
                    position: 'absolute', top: -8, right: 10,
                    fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                    background: p.badge === 'Most popular' ? '#8B5CF6' : '#10B981',
                    color: '#fff', textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>{p.badge}</span>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{p.label}</div>
                  <div style={{ fontSize: 11, color: theme.colors.textLight }}>
                    {p.qty} envelopes · ${(p.price / p.qty).toFixed(2)} each
                  </div>
                </div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>${p.price}</div>
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose}
            style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${theme.colors.border}`, background: '#fff', cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            disabled={submitting}
            onClick={async () => { setSubmitting(true); await onBuy(selected); setSubmitting(false); }}
            style={{
              padding: '8px 14px', borderRadius: 8, border: 'none',
              background: theme.colors.primary, color: '#fff', fontWeight: 600, cursor: 'pointer',
            }}>
            {submitting ? 'Adding…' : `Buy ${selected} for $${packs.find(p => p.qty === selected)?.price || '?'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = r.result;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// ── Shared UI bits ──────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
    </div>
  );
}
function DateRow({ label, value, danger, highlight }) {
  return (
    <div style={fieldRow()}>
      <div style={fieldLabel()}>{label}</div>
      <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: danger ? theme.colors.danger : highlight ? theme.colors.primary : theme.colors.text }}>
        {value ? fmtDate(value) : '—'}
      </div>
    </div>
  );
}
function EditableField({ deal, field, label, onUpdate, inline, compact }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(deal[field] || '');
  const [prev, setPrev] = useState(deal[field]);
  if (!editing && prev !== deal[field]) {
    setPrev(deal[field]);
    setVal(deal[field] || '');
  }

  // Inline mode: name field inside party card header (no label, bigger text)
  if (inline) {
    if (!editing) {
      return (
        <button onClick={() => setEditing(true)} style={{
          textAlign: 'left', padding: '2px 6px', borderRadius: 6, margin: '-2px -6px',
          background: 'transparent', border: '1px solid transparent', cursor: 'pointer',
          fontSize: 14, fontWeight: 700, color: deal[field] ? theme.colors.text : theme.colors.textLight,
          display: 'block', width: '100%',
        }} onMouseEnter={e => e.currentTarget.style.background = '#F5F7FA'}
           onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          {deal[field] || 'Click to add name'}
        </button>
      );
    }
    return (
      <input autoFocus value={val} onChange={e => setVal(e.target.value)}
        onBlur={() => { onUpdate({ [field]: val || null }); setEditing(false); }}
        onKeyDown={e => {
          if (e.key === 'Enter') { onUpdate({ [field]: val || null }); setEditing(false); }
          if (e.key === 'Escape') { setVal(deal[field] || ''); setEditing(false); }
        }}
        style={{ ...fieldInput(), width: '100%', fontSize: 14, fontWeight: 700, padding: '2px 6px', margin: '-2px -6px' }}
      />
    );
  }

  // Compact mode: field inside party card body (small label + value on one line)
  if (compact) {
    if (!editing) {
      return (
        <button onClick={() => setEditing(true)} style={{
          flex: 1, textAlign: 'left', padding: '3px 6px', borderRadius: 6,
          background: 'transparent', border: '1px solid transparent', cursor: 'pointer',
          fontSize: 12.5, color: deal[field] ? theme.colors.text : theme.colors.textLight,
        }} onMouseEnter={e => e.currentTarget.style.background = '#F5F7FA'}
           onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          {deal[field] || `Add ${label.toLowerCase()}`}
        </button>
      );
    }
    return (
      <input autoFocus value={val} onChange={e => setVal(e.target.value)}
        onBlur={() => { onUpdate({ [field]: val || null }); setEditing(false); }}
        onKeyDown={e => {
          if (e.key === 'Enter') { onUpdate({ [field]: val || null }); setEditing(false); }
          if (e.key === 'Escape') { setVal(deal[field] || ''); setEditing(false); }
        }}
        style={{ ...fieldInput(), flex: 1, fontSize: 12.5, padding: '3px 6px' }}
        placeholder={label}
      />
    );
  }

  // Default mode: label + value row (used in Commission tab etc.)
  if (!editing) {
    return (
      <div style={fieldRow()}>
        <div style={fieldLabel()}>{label}</div>
        <button onClick={() => setEditing(true)} style={{
          flex: 1, textAlign: 'left', padding: '6px 8px', borderRadius: 6,
          background: 'transparent', border: '1px solid transparent', cursor: 'pointer',
          fontSize: 13, color: theme.colors.text,
        }} onMouseEnter={e => e.currentTarget.style.background = '#F5F7FA'}
           onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          {deal[field] || <span style={{ color: theme.colors.textLight }}>— click to add</span>}
        </button>
      </div>
    );
  }
  return (
    <div style={fieldRow()}>
      <div style={fieldLabel()}>{label}</div>
      <input autoFocus value={val} onChange={e => setVal(e.target.value)}
        onBlur={() => { onUpdate({ [field]: val || null }); setEditing(false); }}
        onKeyDown={e => {
          if (e.key === 'Enter') { onUpdate({ [field]: val || null }); setEditing(false); }
          if (e.key === 'Escape') { setVal(deal[field] || ''); setEditing(false); }
        }}
        style={{ ...fieldInput(), flex: 1 }}
      />
    </div>
  );
}
function MoneyField({ deal, onUpdate, field, label }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(deal[field] ?? '');
  const [prev, setPrev] = useState(deal[field]);
  if (!editing && prev !== deal[field]) {
    setPrev(deal[field]);
    setVal(deal[field] ?? '');
  }
  return (
    <div style={fieldRow()}>
      <div style={fieldLabel()}>{label}</div>
      {editing ? (
        <input autoFocus type="number" value={val} onChange={e => setVal(e.target.value)}
          onBlur={() => {
            const num = val === '' ? null : parseFloat(val);
            onUpdate({ [field]: Number.isFinite(num) ? num : null });
            setEditing(false);
          }}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setVal(deal[field] ?? ''); setEditing(false); } }}
          style={{ ...fieldInput(), flex: 1 }}
        />
      ) : (
        <button onClick={() => setEditing(true)} style={{
          flex: 1, textAlign: 'left', padding: '6px 8px', borderRadius: 6,
          background: 'transparent', border: '1px solid transparent', cursor: 'pointer',
          fontSize: 13, color: theme.colors.text,
        }}>
          {deal[field] != null ? fmtMoney(deal[field]) : <span style={{ color: theme.colors.textLight }}>—</span>}
        </button>
      )}
    </div>
  );
}
function NumberField({ deal, onUpdate, field, label, suffix }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(deal[field] ?? '');
  const [prev, setPrev] = useState(deal[field]);
  if (!editing && prev !== deal[field]) {
    setPrev(deal[field]);
    setVal(deal[field] ?? '');
  }
  return (
    <div style={fieldRow()}>
      <div style={fieldLabel()}>{label}</div>
      {editing ? (
        <input autoFocus type="number" step="0.01" value={val} onChange={e => setVal(e.target.value)}
          onBlur={() => {
            const num = val === '' ? null : parseFloat(val);
            onUpdate({ [field]: Number.isFinite(num) ? num : null });
            setEditing(false);
          }}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditing(false); }}
          style={{ ...fieldInput(), flex: 1 }}
        />
      ) : (
        <button onClick={() => setEditing(true)} style={{
          flex: 1, textAlign: 'left', padding: '6px 8px', borderRadius: 6,
          background: 'transparent', border: '1px solid transparent', cursor: 'pointer',
          fontSize: 13, color: theme.colors.text,
        }}>
          {deal[field] != null ? `${deal[field]}${suffix || ''}` : <span style={{ color: theme.colors.textLight }}>—</span>}
        </button>
      )}
    </div>
  );
}
function PayoutRow({ label, value, color, big }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${theme.colors.border}` }}>
      <div style={{ fontSize: big ? 13 : 12, fontWeight: big ? 700 : 600, color: theme.colors.text }}>{label}</div>
      <div style={{ fontSize: big ? 16 : 14, fontWeight: 800, color }}>{fmtMoney(value)}</div>
    </div>
  );
}
function StatusBadge({ stat }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '2px 7px',
      borderRadius: 6, fontSize: 10, fontWeight: 700,
      background: `${stat.color}18`, color: stat.color,
    }}>{stat.label}</span>
  );
}
function Center({ children }) {
  return <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textLight, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>{children}</div>;
}

// ══════════════════════════════════════════════════════════════
// FEATURE 1: Deal Task Templates with milestone-relative dates
// ══════════════════════════════════════════════════════════════

const RESIDENTIAL_TASK_TEMPLATES = [
  { task: 'Send executed contract to all parties', anchor: 'attorney_review_start', offset: 0, phase: 'Under Contract' },
  { task: 'Order title search', anchor: 'attorney_review_start', offset: 1, phase: 'Under Contract' },
  { task: 'Obtain 7-day financing contingency', anchor: 'attorney_review_start', offset: 7, phase: 'Under Contract' },
  { task: 'Schedule home inspection', anchor: 'attorney_review_start', offset: 10, phase: 'Attorney Review' },
  { task: 'Review inspection report with client', anchor: 'inspection_date', offset: 2, phase: 'Inspection' },
  { task: 'Submit repair request (if any)', anchor: 'inspection_date', offset: 5, phase: 'Inspection' },
  { task: 'Order appraisal', anchor: 'inspection_date', offset: 3, phase: 'Appraisal' },
  { task: 'Confirm mortgage commitment letter', anchor: 'mortgage_commitment_date', offset: 0, phase: 'Financing' },
  { task: 'Review title commitment', anchor: 'mortgage_commitment_date', offset: 3, phase: 'Financing' },
  { task: 'Schedule final walkthrough', anchor: 'closing_date', offset: -2, phase: 'Closing' },
  { task: 'Confirm closing date, time & location', anchor: 'closing_date', offset: -3, phase: 'Closing' },
  { task: 'Send closing gift', anchor: 'closing_date', offset: 2, phase: 'Post-Close' },
  { task: 'Request review/testimonial', anchor: 'closing_date', offset: 7, phase: 'Post-Close' },
];

const COMMERCIAL_TASK_TEMPLATES = [
  { task: 'Draft and circulate LOI', anchor: 'attorney_review_start', offset: 0, phase: 'LOI' },
  { task: 'Negotiate LOI terms', anchor: 'attorney_review_start', offset: 5, phase: 'LOI' },
  { task: 'Order Environmental Phase I', anchor: 'inspection_date', offset: 0, phase: 'Due Diligence' },
  { task: 'Order ALTA survey', anchor: 'inspection_date', offset: 3, phase: 'Due Diligence' },
  { task: 'Verify zoning compliance', anchor: 'inspection_date', offset: 5, phase: 'Due Diligence' },
  { task: 'Review rent roll & operating expenses', anchor: 'inspection_date', offset: 7, phase: 'Due Diligence' },
  { task: 'Building condition assessment', anchor: 'inspection_date', offset: 10, phase: 'Due Diligence' },
  { task: 'ADA compliance review', anchor: 'inspection_date', offset: 10, phase: 'Due Diligence' },
  { task: 'Negotiate purchase agreement', anchor: 'attorney_review_end', offset: 0, phase: 'Purchase Agreement' },
  { task: 'Collect tenant estoppel certificates', anchor: 'mortgage_commitment_date', offset: -10, phase: 'Estoppels' },
  { task: 'Execute SNDA agreements', anchor: 'mortgage_commitment_date', offset: -5, phase: 'Estoppels' },
  { task: 'Submit loan package to lender', anchor: 'mortgage_commitment_date', offset: -14, phase: 'Financing' },
  { task: 'Commercial appraisal review', anchor: 'mortgage_commitment_date', offset: 0, phase: 'Financing' },
  { task: 'Final walkthrough', anchor: 'closing_date', offset: -2, phase: 'Closing' },
  { task: 'Wire transfer confirmation', anchor: 'closing_date', offset: -1, phase: 'Closing' },
];

function calcDueDate(deal, anchor, offset) {
  const base = deal[anchor];
  if (!base) return null;
  const d = new Date(base + 'T00:00:00');
  d.setDate(d.getDate() + offset);
  return d.toISOString().split('T')[0];
}

function DealTasksTab({ deal, showToast }) {
  const templates = isCommercialDeal(deal) ? COMMERCIAL_TASK_TEMPLATES : RESIDENTIAL_TASK_TEMPLATES;
  const [checkedTasks, setCheckedTasks] = useState({});

  const toggle = (idx) => setCheckedTasks(prev => ({ ...prev, [idx]: !prev[idx] }));
  const today = new Date().toISOString().split('T')[0];

  const grouped = {};
  templates.forEach((t, i) => {
    if (!grouped[t.phase]) grouped[t.phase] = [];
    grouped[t.phase].push({ ...t, idx: i });
  });

  const done = Object.values(checkedTasks).filter(Boolean).length;

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text }}>Deal Checklist</div>
          <div style={{ fontSize: 11, color: theme.colors.textLight }}>{done} of {templates.length} completed</div>
        </div>
        <div style={{
          width: 80, height: 6, borderRadius: 3, background: theme.colors.border,
        }}>
          <div style={{ width: `${(done / templates.length) * 100}%`, height: '100%', borderRadius: 3, background: theme.colors.success, transition: 'width 0.3s ease' }} />
        </div>
      </div>

      {Object.entries(grouped).map(([phase, tasks]) => (
        <div key={phase} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.colors.primary, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, paddingBottom: 4, borderBottom: `1px solid ${theme.colors.border}` }}>{phase}</div>
          {tasks.map(t => {
            const due = calcDueDate(deal, t.anchor, t.offset);
            const overdue = due && due < today && !checkedTasks[t.idx];
            return (
              <div key={t.idx} onClick={() => toggle(t.idx)} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', marginBottom: 4,
                borderRadius: 8, cursor: 'pointer', background: checkedTasks[t.idx] ? '#F0FDF4' : overdue ? '#FEF2F2' : 'transparent',
                border: `1px solid ${overdue ? '#FECACA' : 'transparent'}`,
              }}>
                <div style={{
                  width: 20, height: 20, borderRadius: 6, border: `2px solid ${checkedTasks[t.idx] ? theme.colors.success : theme.colors.border}`,
                  background: checkedTasks[t.idx] ? theme.colors.success : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {checkedTasks[t.idx] && <Check size={12} color="white" />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: checkedTasks[t.idx] ? theme.colors.textLight : theme.colors.text, textDecoration: checkedTasks[t.idx] ? 'line-through' : 'none' }}>{t.task}</div>
                </div>
                <div style={{ fontSize: 10, color: overdue ? theme.colors.danger : theme.colors.textLight, fontWeight: overdue ? 700 : 500, flexShrink: 0 }}>
                  {due ? (overdue ? `Overdue · ${fmtDate(due)}` : fmtDate(due)) : 'Date TBD'}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// FEATURE 2: Per-deal Document Checklist / Paperwork Workspace
// ══════════════════════════════════════════════════════════════


/* PaperworkTab removed — merged into DocsTab above */

// ══════════════════════════════════════════════════════════════
// FEATURE 3: Lost Deal Workflow
// ══════════════════════════════════════════════════════════════

function LostDealModal({ deal, onClose, showToast }) {
  const [stillWorking, setStillWorking] = useState(true);
  const [copyItems, setCopyItems] = useState({ parties: true, notes: true, docs: true, commission: false });

  const toggle = (key) => setCopyItems(prev => ({ ...prev, [key]: !prev[key] }));

  const handleRestart = () => {
    showToast?.('New deal created with client details carried over');
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, padding: 24, width: 440, maxWidth: '95vw', boxShadow: theme.shadow.lg }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RotateCcw size={20} color="#F59E0B" />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text }}>Deal Lost</div>
            <div style={{ fontSize: 12, color: theme.colors.textLight }}>This deal has been marked as {deal.status === 'fell_through' ? 'fell through' : 'cancelled'}</div>
          </div>
        </div>

        {/* Still working toggle */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 16px', borderRadius: 10, background: '#F8FAFC', marginBottom: 16,
          border: `1px solid ${theme.colors.border}`,
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>Still working with this client?</span>
          <button onClick={() => setStillWorking(!stillWorking)} style={{
            width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
            background: stillWorking ? theme.colors.success : '#D1D5DB', position: 'relative', transition: 'background 0.2s',
          }}>
            <div style={{
              width: 18, height: 18, borderRadius: 9, background: 'white', position: 'absolute', top: 3,
              left: stillWorking ? 23 : 3, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </button>
        </div>

        {stillWorking && (
          <>
            <div style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 12 }}>
              Select what to carry over to a new deal. Client info is always preserved.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              {[
                { key: 'parties', label: 'Parties', desc: 'Co-op agent, attorney, lender, title company' },
                { key: 'notes', label: 'Notes & timeline', desc: 'All team notes and activity history' },
                { key: 'dealroom', label: 'Paperwork', desc: 'Documents uploaded to this deal' },
                { key: 'commission', label: 'Commission structure', desc: 'Split percentages and payout terms' },
              ].map(item => (
                <div key={item.key} onClick={() => toggle(item.key)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  borderRadius: 8, cursor: 'pointer', border: `1px solid ${copyItems[item.key] ? theme.colors.primary + '40' : theme.colors.border}`,
                  background: copyItems[item.key] ? `${theme.colors.primary}06` : 'white',
                }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: 4, flexShrink: 0,
                    border: `2px solid ${copyItems[item.key] ? theme.colors.primary : theme.colors.border}`,
                    background: copyItems[item.key] ? theme.colors.primary : 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {copyItems[item.key] && <Check size={12} color="white" />}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>{item.label}</div>
                    <div style={{ fontSize: 11, color: theme.colors.textLight }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <button onClick={handleRestart} style={{
              width: '100%', padding: '12px', borderRadius: 10, border: 'none',
              background: theme.colors.primary, color: 'white', fontSize: 14, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              <RotateCcw size={16} /> Start New Deal with Same Client
            </button>
          </>
        )}

        <button onClick={onClose} style={{
          width: '100%', padding: '10px', borderRadius: 10, marginTop: 8,
          border: `1px solid ${theme.colors.border}`, background: 'white',
          color: theme.colors.textLight, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>
          {stillWorking ? 'Skip for Now' : 'Close'}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// FEATURE 4: Compliance Tab with Broker File Review
// ══════════════════════════════════════════════════════════════

const RESIDENTIAL_COMPLIANCE = [
  { key: 'contract_signed', label: 'Fully executed contract on file', required: true },
  { key: 'disclosure_signed', label: 'Seller disclosure signed by all parties', required: true },
  { key: 'lead_paint', label: 'Lead-based paint disclosure (pre-1978)', required: true },
  { key: 'agency_disclosure', label: 'Agency disclosure form signed', required: true },
  { key: 'wire_fraud_notice', label: 'Wire fraud warning acknowledged', required: true },
  { key: 'earnest_money', label: 'Earnest money deposit received & receipted', required: true },
  { key: 'attorney_review_complete', label: 'Attorney review period completed', required: true },
  { key: 'inspection_contingency', label: 'Inspection contingency resolved', required: true },
  { key: 'mortgage_contingency', label: 'Mortgage contingency cleared', required: true },
  { key: 'title_clear', label: 'Title search — no unresolved liens', required: true },
  { key: 'final_walkthrough', label: 'Final walkthrough completed', required: false },
  { key: 'closing_docs_reviewed', label: 'Closing documents reviewed by broker', required: true },
];

const COMMERCIAL_COMPLIANCE = [
  { key: 'loi_signed', label: 'LOI executed by all parties', required: true },
  { key: 'purchase_agreement', label: 'Purchase agreement fully executed', required: true },
  { key: 'agency_disclosure', label: 'Agency/dual agency disclosure signed', required: true },
  { key: 'wire_fraud_notice', label: 'Wire fraud warning acknowledged', required: true },
  { key: 'earnest_money', label: 'Earnest money deposit verified', required: true },
  { key: 'environmental_clear', label: 'Environmental reports reviewed — no issues', required: true },
  { key: 'zoning_confirmed', label: 'Zoning compliance confirmed', required: true },
  { key: 'estoppels_complete', label: 'All tenant estoppels collected', required: true },
  { key: 'rent_roll_verified', label: 'Rent roll verified against estoppels', required: true },
  { key: 'insurance_binder', label: 'Commercial insurance binder received', required: true },
  { key: 'loan_docs_received', label: 'Loan commitment docs on file', required: true },
  { key: 'closing_docs_reviewed', label: 'Closing documents reviewed by broker', required: true },
  { key: 'entity_docs', label: 'Entity formation docs (if LLC/Corp buyer)', required: false },
];

function ComplianceTab({ deal, showToast }) {
  const items = isCommercialDeal(deal) ? COMMERCIAL_COMPLIANCE : RESIDENTIAL_COMPLIANCE;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [milestoneStatus, setMilestoneStatus] = useState({});

  // Load deal documents + milestone status to auto-check compliance items
  const load = useCallback(async () => {
    if (!deal?.id) return;
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const [rDocs, rMilestones] = await Promise.all([
        fetch(`/api/deal-documents?deal_id=${deal.id}`, { headers: { Authorization: `Bearer ${session.access_token}` } }),
        fetch(`/api/deal-verify?deal_id=${deal.id}&action=milestone_status`, { headers: { Authorization: `Bearer ${session.access_token}` } }),
      ]);
      if (rDocs.ok) { const d = await rDocs.json(); setDocs(d.documents || []); }
      if (rMilestones.ok) { const m = await rMilestones.json(); setMilestoneStatus(m.milestones || {}); }
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [deal?.id]);

  useEffect(() => { load(); }, [load]);

  // Auto-determine compliance item status from deal state
  // Simple doc name matching helper (must be before getItemStatus which uses it)
  const matchDocNameToKey = (docName, key) => {
    if (!docName) return false;
    const name = docName.toLowerCase();
    const kws = key.replace(/_/g, ' ').split(' ').filter(w => w.length > 2);
    return kws.filter(kw => name.includes(kw)).length >= 1;
  };

  const getItemStatus = (item) => {
    const key = item.key;
    // Map compliance items to evidence
    const docKeys = {
      contract_signed: ['contract'],
      disclosure_signed: ['disclosure'],
      lead_paint: ['lead_paint'],
      agency_disclosure: [],
      wire_fraud_notice: [],
      earnest_money: [],
      attorney_review_complete: [],
      inspection_contingency: [],
      mortgage_contingency: [],
      title_clear: ['title_commitment'],
      final_walkthrough: [],
      closing_docs_reviewed: ['closing_disclosure'],
      // Commercial
      loi_signed: ['loi'],
      purchase_agreement: ['purchase_agreement'],
      environmental_clear: ['env_phase1'],
      zoning_confirmed: ['zoning_letter'],
      estoppels_complete: ['estoppels'],
      rent_roll_verified: ['rent_roll'],
      insurance_binder: [],
      loan_docs_received: ['loan_commitment'],
      entity_docs: [],
    };

    const milestoneKeys = {
      attorney_review_complete: 'attorney_review',
      inspection_contingency: 'inspection',
      mortgage_contingency: 'mortgage_commitment',
      final_walkthrough: 'clear_to_close',
    };

    // Check if associated documents are verified + signed
    const requiredDocKeys = docKeys[key] || [];
    if (requiredDocKeys.length > 0) {
      const allDocsComplete = requiredDocKeys.every(dk => {
        const doc = docs.find(d => d.checklist_key === dk || matchDocNameToKey(d.name, dk));
        return doc && doc.verification_status === 'verified' && (doc.signing_status === 'signed' || doc.signing_status === 'unsigned');
      });
      if (allDocsComplete) return 'complete';
      const anyUploaded = requiredDocKeys.some(dk => docs.find(d => d.checklist_key === dk || matchDocNameToKey(d.name, dk)));
      if (anyUploaded) return 'in_progress';
    }

    // Check if associated milestone is done
    const msKey = milestoneKeys[key];
    if (msKey) {
      const ms = milestoneStatus[msKey];
      if (ms?.completable) return 'complete';
      if (ms?.progress?.fulfilled > 0) return 'in_progress';
    }

    // Check deal fields for date-based completion
    const dateChecks = {
      attorney_review_complete: deal.attorney_review_end,
      inspection_contingency: deal.inspection_date,
      mortgage_contingency: deal.mortgage_commitment_date,
      earnest_money: deal.earnest_money_received,
      final_walkthrough: deal.clear_to_close_date,
    };
    if (dateChecks[key]) return 'complete';

    return 'pending';
  };

  const reqItems = items.filter(i => i.required);
  const checkedItems = items.filter(i => getItemStatus(i) === 'complete');
  const reqChecked = reqItems.filter(i => getItemStatus(i) === 'complete').length;
  const allReqMet = reqChecked === reqItems.length;
  const progressPct = reqItems.length > 0 ? Math.round((reqChecked / reqItems.length) * 100) : 100;

  return (
    <div style={{ padding: 18 }}>
      {/* Summary card */}
      <div style={{
        padding: '14px 16px', borderRadius: 12, marginBottom: 16,
        background: allReqMet ? '#F0FDF4' : '#FEF3C7',
        border: `1px solid ${allReqMet ? '#10B98130' : '#F59E0B30'}`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Shield size={14} color={allReqMet ? '#065F46' : '#92400E'} />
              {allReqMet ? 'All compliance requirements met' : `${reqItems.length - reqChecked} required items pending`}
            </div>
            <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 3 }}>
              {checkedItems.length} of {items.length} total items complete
            </div>
          </div>
          <div style={{
            width: 42, height: 42, borderRadius: 21,
            background: allReqMet ? '#BBF7D0' : '#FDE68A',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 800, color: allReqMet ? '#065F46' : '#92400E',
          }}>{progressPct}%</div>
        </div>
        {/* Progress bar */}
        <div style={{ height: 6, borderRadius: 3, background: allReqMet ? '#D1FAE5' : '#FDE68A', overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 3, background: allReqMet ? theme.colors.success : '#F59E0B', width: `${progressPct}%`, transition: 'width 0.5s ease' }} />
        </div>
      </div>

      <div style={{
        padding: '8px 10px', borderRadius: 8, marginBottom: 14,
        background: `${theme.colors.primary}05`, border: `1px solid ${theme.colors.primary}15`,
        fontSize: 11, color: theme.colors.textLight, lineHeight: 1.5,
        display: 'flex', alignItems: 'flex-start', gap: 6,
      }}>
        <Shield size={12} color={theme.colors.primary} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>
          This checklist updates automatically based on your documents, verifications, and milestones.
          Items check themselves when the underlying evidence is complete — no manual input needed.
        </span>
      </div>

      {loading ? (
        <Center><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading compliance status…</Center>
      ) : (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.colors.text, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
            Compliance Checklist ({checkedItems.length}/{items.length})
          </div>
          {items.map(item => {
            const status = getItemStatus(item);
            return (
              <div key={item.key} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', marginBottom: 4,
                borderRadius: 8,
                background: status === 'complete' ? '#F0FDF4' : status === 'in_progress' ? '#FFFBEB' : theme.colors.card,
                border: `1px solid ${status === 'complete' ? '#10B98120' : status === 'in_progress' ? '#F59E0B20' : theme.colors.border}`,
              }}>
                <div style={{
                  width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                  border: `2px solid ${status === 'complete' ? theme.colors.success : status === 'in_progress' ? '#F59E0B' : theme.colors.border}`,
                  background: status === 'complete' ? theme.colors.success : status === 'in_progress' ? '#F59E0B' : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {status === 'complete' && <Check size={12} color="white" />}
                  {status === 'in_progress' && <div style={{ width: 5, height: 5, borderRadius: 3, background: 'white' }} />}
                </div>
                <div style={{ flex: 1, fontSize: 13, fontWeight: 500, color: status === 'complete' ? theme.colors.textLight : theme.colors.text }}>
                  {item.label}
                </div>
                {item.required && status === 'pending' && (
                  <span style={{ fontSize: 9, fontWeight: 700, color: theme.colors.danger, textTransform: 'uppercase' }}>Pending</span>
                )}
                {status === 'in_progress' && (
                  <span style={{ fontSize: 9, fontWeight: 700, color: '#F59E0B', textTransform: 'uppercase' }}>In progress</span>
                )}
                {status === 'complete' && (
                  <span style={{ fontSize: 9, fontWeight: 700, color: theme.colors.success, textTransform: 'uppercase' }}>Done</span>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════

function nextMilestone(deal) {
  const flow = getMilestoneFlow(deal);
  const currentIdx = flow.findIndex(m => m.key === deal.status);
  for (let i = 0; i < flow.length; i++) {
    const m = flow[i];
    // A milestone is done if it has a date OR the deal has advanced past it
    const done = !!deal[m.dateCol] || (currentIdx >= 0 && i < currentIdx);
    if (!done) return m;
  }
  return null;
}
function isOverdue(dateStr, currentStatus) {
  if (!dateStr) return false;
  if (currentStatus === 'closed' || currentStatus === 'clear_to_close') return false;
  const today = new Date().toISOString().split('T')[0];
  return dateStr < today;
}
function fmtDate(d) {
  if (!d) return '—';
  try { return formatDateShort(d, true); }
  catch { return d; }
}
function fmtMoney(n) {
  const num = Number(n) || 0;
  return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}
function fieldRow() { return { display: 'flex', alignItems: 'center', gap: 10 }; }
function fieldLabel() { return { width: 120, fontSize: 11, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }; }
function fieldInput() { return { padding: '7px 10px', borderRadius: 8, border: `1px solid ${theme.colors.border}`, fontSize: 13, color: theme.colors.text, background: theme.colors.card, boxSizing: 'border-box' }; }
function iconBtn() { return { background: 'transparent', border: `1px solid ${theme.colors.border}`, color: theme.colors.text, borderRadius: 8, width: 30, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }; }
function primaryBtn() { return { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 14px', borderRadius: 8, border: 'none', background: theme.colors.primary, color: 'white', fontSize: 12, fontWeight: 700, cursor: 'pointer' }; }
function ghostBtn() { return { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 12px', borderRadius: 8, background: 'transparent', border: `1px solid ${theme.colors.border}`, color: theme.colors.text, fontSize: 12, fontWeight: 600, cursor: 'pointer' }; }
function miniBtn() { return { width: 26, height: 26, borderRadius: 6, border: `1px solid ${theme.colors.border}`, background: 'white', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textLight, padding: 0, fontSize: 11 }; }
